import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { SITE_BASE_URL } from '../../config/environments/api';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip, Legend } from 'recharts';
import ReactECharts from 'echarts-for-react';
import Flag from 'react-world-flags';
import { MapContainer, GeoJSON, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { API_BASE } from '../../config/environments/api';
import './CountryEnergyProfile.scss';

// Hook to animate number increase
const useAnimatedNumber = (formattedValue, shouldAnimate = true, animationSpeed = 15) => {
  const [displayValue, setDisplayValue] = useState(formattedValue);
  const previousValueRef = useRef(formattedValue);
  const animationRef = useRef(null);

  useEffect(() => {
    // Store the original formatted value to use as template
    const originalValue = formattedValue;
    
    // Parse numeric value from formatted string
    const parseNumericValue = (val) => {
      if (val === null || val === undefined || val === 'N/A' || val === '' || val === '0') return 0;
      
      // Remove commas, spaces, and extract number part
      const cleaned = String(val).replace(/[,\s]/g, '');
      
      // Handle percentage, million (M), billion (B), etc.
      let multiplier = 1;
      let numStr = cleaned;
      
      if (cleaned.includes('M') && !cleaned.includes('(M)')) {
        multiplier = 1000000;
        numStr = cleaned.replace('M', '');
      } else if (cleaned.includes('B')) {
        multiplier = 1000000000;
        numStr = cleaned.replace('B', '');
      } else if (cleaned.includes('K')) {
        multiplier = 1000;
        numStr = cleaned.replace('K', '');
      } else if (cleaned.includes('%')) {
        numStr = cleaned.replace('%', '');
      }
      
      const num = parseFloat(numStr);
      return isNaN(num) ? 0 : num * multiplier;
    };

    const startValue = parseNumericValue(previousValueRef.current);
    const endValue = parseNumericValue(formattedValue);
    
    // If values are the same or animation disabled, update immediately
    if (!shouldAnimate || startValue === endValue || formattedValue === 'N/A' || previousValueRef.current === 'N/A') {
      setDisplayValue(formattedValue);
      previousValueRef.current = formattedValue;
      return;
    }

    // Clear any existing animation
    if (animationRef.current) {
      clearInterval(animationRef.current);
    }

    // Calculate steps needed based on difference
    const difference = endValue - startValue;
    const absDifference = Math.abs(difference);
    
    // Calculate steps: more steps for larger differences, but cap at reasonable number
    let steps;
    if (absDifference < 100) {
      steps = Math.max(10, Math.floor(absDifference / 5));
    } else if (absDifference < 1000) {
      steps = Math.max(20, Math.floor(absDifference / 20));
    } else if (absDifference < 10000) {
      steps = Math.max(30, Math.floor(absDifference / 50));
    } else {
      steps = Math.max(40, Math.min(80, Math.floor(absDifference / 200)));
    }
    
    const stepSize = difference / steps;
    let currentStep = 0;

    // Format value during animation - preserve original format
    const formatAnimatedValue = (num) => {
      const originalStr = String(originalValue);
      
      // If original has 'M' (not in parentheses), format as million
      if (originalStr.includes('M') && !originalStr.includes('(M)')) {
        const millions = num / 1000000;
        return millions.toFixed(2) + 'M';
      }
      // If original has 'B', format as billion
      if (originalStr.includes('B')) {
        const billions = num / 1000000000;
        return billions.toFixed(2) + 'B';
      }
      // If original has 'K', format as thousand
      if (originalStr.includes('K')) {
        const thousands = num / 1000;
        return thousands.toFixed(2) + 'K';
      }
      // If original has '%', format as percentage
      if (originalStr.includes('%')) {
        return num.toFixed(2) + '%';
      }
      
      // Default: format with commas (Indian numbering system style)
      // Try to match the format from formatIndianNumber
      const numStr = num.toFixed(2);
      const parts = numStr.split('.');
      let integerPart = parts[0];
      const decimalPart = parts[1] || '';
      
      // Add commas in Indian format: first comma after 3 digits, then every 2 digits
      let formatted = '';
      const len = integerPart.length;
      
      if (len <= 3) {
        formatted = integerPart;
      } else {
        // First 3 digits from right
        formatted = integerPart.slice(-3);
        integerPart = integerPart.slice(0, -3);
        
        // Then add commas every 2 digits
        while (integerPart.length > 0) {
          if (integerPart.length >= 2) {
            formatted = integerPart.slice(-2) + ',' + formatted;
            integerPart = integerPart.slice(0, -2);
          } else {
            formatted = integerPart + ',' + formatted;
            integerPart = '';
          }
        }
      }
      
      // Add decimal part if exists (only if number has decimals)
      return decimalPart ? formatted + '.' + decimalPart : formatted;
    };

    // Animate the number
    animationRef.current = setInterval(() => {
      currentStep++;
      const currentValue = startValue + (stepSize * currentStep);
      
      if (currentStep >= steps) {
        setDisplayValue(formattedValue);
        previousValueRef.current = formattedValue;
        if (animationRef.current) {
          clearInterval(animationRef.current);
          animationRef.current = null;
        }
      } else {
        setDisplayValue(formatAnimatedValue(currentValue));
      }
    }, animationSpeed);

    // Cleanup
    return () => {
      if (animationRef.current) {
        clearInterval(animationRef.current);
        animationRef.current = null;
      }
    };
  }, [formattedValue, shouldAnimate, animationSpeed]);

  return displayValue;
};

// Fix for default markers in react-leaflet
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

// SVG Icon Component - renders SVG inline so we can style paths with CSS
const SVGIcon = ({ src, className, color, alt }) => {
  const [svgContent, setSvgContent] = React.useState(null);

  React.useEffect(() => {
    fetch(src)
      .then(res => res.text())
      .then(text => {
        // Remove XML declaration and extract SVG content
        const parser = new DOMParser();
        const doc = parser.parseFromString(text, 'image/svg+xml');
        const svg = doc.querySelector('svg');
        if (svg) {
          // Remove fill attributes from paths so CSS can control them
          svg.querySelectorAll('path').forEach(path => {
            path.removeAttribute('fill');
          });
          setSvgContent(svg.innerHTML);
        }
      })
      .catch(err => {});
  }, [src]);

  if (!svgContent) return <span className={className} style={{ width: '24px', height: '24px', display: 'inline-block' }} />;

  return (
    <svg
      width="24"
      height="24"
      viewBox="0 0 512 512"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
      style={{ 'fill': color }}
      aria-label={alt}
    >
      <g dangerouslySetInnerHTML={{ __html: svgContent }} />
    </svg>
  );
};

// Helper function to convert country name to URL-friendly format (lowercase, hyphenated)
const countryToUrl = (countryName) => {
  if (!countryName) return '';
  return countryName.toLowerCase().replace(/\s+/g, '-');
};

// Helper function to convert URL format back to country name
const urlToCountry = (urlCountry, availableCountries) => {
  if (!urlCountry) return null;
  // Convert hyphenated lowercase back to proper case by matching with available countries
  const urlLower = urlCountry.toLowerCase();
  const matchedCountry = availableCountries.find(country =>
    countryToUrl(country) === urlLower
  );
  return matchedCountry || null;
};

// Helper function to convert country name to ISO country code
const countryToISO = (countryName) => {
  if (!countryName) return null;

  const countryCodeMap = {
    'Algeria': 'DZ',
    'Angola': 'AO',
    'Benin': 'BJ',
    'Botswana': 'BW',
    'Burkina Faso': 'BF',
    'Burundi': 'BI',
    'Cameroon': 'CM',
    'Cape Verde': 'CV',
    'Central African Republic': 'CF',
    'Chad': 'TD',
    'Comoros': 'KM',
    'Congo': 'CG',
    'Côte d\'Ivoire': 'CI',
    'Democratic Republic of the Congo': 'CD',
    'Egypt': 'EG',
    'Equatorial Guinea': 'GQ',
    'Eritrea': 'ER',
    'Eswatini': 'SZ',
    'Ethiopia': 'ET',
    'Gabon': 'GA',
    'Gambia': 'GM',
    'Ghana': 'GH',
    'Guinea': 'GN',
    'Guinea-Bissau': 'GW',
    'Kenya': 'KE',
    'Lesotho': 'LS',
    'Liberia': 'LR',
    'Libya': 'LY',
    'Madagascar': 'MG',
    'Malawi': 'MW',
    'Mali': 'ML',
    'Mauritania': 'MR',
    'Mauritius': 'MU',
    'Morocco': 'MA',
    'Mozambique': 'MZ',
    'Namibia': 'NA',
    'Niger': 'NE',
    'Nigeria': 'NG',
    'Rwanda': 'RW',
    'São Tomé and Príncipe': 'ST',
    'Senegal': 'SN',
    'Seychelles': 'SC',
    'Sierra Leone': 'SL',
    'Somalia': 'SO',
    'South Africa': 'ZA',
    'South Sudan': 'SS',
    'Sudan': 'SD',
    'Tanzania': 'TZ',
    'Togo': 'TG',
    'Tunisia': 'TN',
    'Uganda': 'UG',
    'Zambia': 'ZM',
    'Zimbabwe': 'ZW'
  };

  return countryCodeMap[countryName] || null;
};

const CountryEnergyProfile = () => {
  const { country: countryParam } = useParams();
  const navigate = useNavigate();
  const [selectedCountry, setSelectedCountry] = useState(null);
  const [selectedYear, setSelectedYear] = useState(null);
  const [availableCountries, setAvailableCountries] = useState([]);
  const [availableYears, setAvailableYears] = useState([]);
  const [loading, setLoading] = useState(false);
  const [countryRegion, setCountryRegion] = useState(null);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [africaData, setAfricaData] = useState(null);
  // Track previous country to detect actual country changes
  const prevCountryRef = useRef(null);
  const dropdownRef = useRef(null);
  // Track if we need to navigate after setting country (to avoid calling navigate during render)
  const shouldNavigateRef = useRef(false);
  // Track if this is the initial page load
  const isInitialLoadRef = useRef(true);
  const [countryData, setCountryData] = useState({
    population: null,
    populationAccessNational: null,
    populationAccessRural: null,
    populationAccessUrban: null,
    cleanCookingAccess: null
  });
  const [dashboardData, setDashboardData] = useState({
    electricityGeneration: null,
    electricityImport: null,
    electricityExport: null,
    gdp: null,
    cleanCooking: null,
    population: null,
    populationWithAccessUrban: null,
    populationWithAccessRural: null,
    populationWithoutAccessUrban: null,
    populationWithoutAccessRural: null,
    perCapitaElectricity: null,
    totalCommitments: null,
    totalProjectsCount: null
  });
  const [electricityTradeToggle, setElectricityTradeToggle] = useState('import'); // 'import' or 'export'
  const [populationAccessToggle, setPopulationAccessToggle] = useState('access'); // 'access' or 'without'
  const [electricityAccessChartToggle, setElectricityAccessChartToggle] = useState('with-access'); // 'with-access' or 'without-access'
  // Removed activeTab state - no longer using tabs
  const [electricityAccessData, setElectricityAccessData] = useState([]);
  const [loadingElectricityData, setLoadingElectricityData] = useState(false);
  // Animation state
  const [shouldAnimate, setShouldAnimate] = useState(false);
  const previousYearRef = useRef(null);
  const previousCountryRef = useRef(null);
  const [cleanCookingData, setCleanCookingData] = useState([]);
  const [loadingCleanCookingData, setLoadingCleanCookingData] = useState(false);
  const [energyEfficiencyData, setEnergyEfficiencyData] = useState([]);
  const [loadingEnergyEfficiencyData, setLoadingEnergyEfficiencyData] = useState(false);
  const [electricityDemandData, setElectricityDemandData] = useState([]);
  const [loadingElectricityDemandData, setLoadingElectricityDemandData] = useState(false);
  const [energyIntensityData, setEnergyIntensityData] = useState([]);
  const [loadingEnergyIntensityData, setLoadingEnergyIntensityData] = useState(false);
  const [electricityGenerationTableData, setElectricityGenerationTableData] = useState([]);
  const [loadingElectricityGenerationTable, setLoadingElectricityGenerationTable] = useState(false);
  const [tableYear, setTableYear] = useState(null);
  const [expandedSolar, setExpandedSolar] = useState(false);
  const [electricityCapacityData, setElectricityCapacityData] = useState([]);
  const [loadingElectricityCapacityData, setLoadingElectricityCapacityData] = useState(false);
  const [maximizedChart, setMaximizedChart] = useState(null);
  const [renewableEnergyGenerationMixData, setRenewableEnergyGenerationMixData] = useState([]);
  const [loadingRenewableEnergyGenerationMixData, setLoadingRenewableEnergyGenerationMixData] = useState(false);

  const isMobile = window.innerWidth <= 480;

  // Fetch available years
  useEffect(() => {
    const fetchYears = async () => {
      try {
        const response = await fetch(`${API_BASE}/years`);
        if (response.ok) {
          const data = await response.json();
          const uniqueYears = [...new Set(data.years.map(year => Number(year)))].sort((a, b) => a - b);
          setAvailableYears(uniqueYears);
          if (uniqueYears.length > 0) {
            // Default to most recent year (last in ascending order)
            setSelectedYear(uniqueYears[uniqueYears.length - 1]);
          }
        }
      } catch (error) {
      }
    };
    fetchYears();
  }, []);

  // // Fetch available countries when year changes
  // useEffect(() => {
  //   if (!selectedYear) return;

  //   const fetchCountries = async () => {
  //     try {
  //       const response = await fetch(`${API_BASE}/data-glance/countries?year=${selectedYear}`);
  //       if (response.ok) {
  //         const data = await response.json();
  //         const countries = (data.countries || []).sort();
  //         setAvailableCountries(countries);

  //         // Check URL for country parameter first
  //         const countryFromUrl = countryParam ? urlToCountry(countryParam, countries) : null;
  //         const validCountryFromUrl = countryFromUrl && countries.includes(countryFromUrl);

  //         // Set default country if none selected or if current selection is not in the list
  //         setSelectedCountry(prevCountry => {
  //           if (countries.length > 0) {
  //             // Priority: URL param > previous selection > first country
  //             if (validCountryFromUrl) {
  //               return countryFromUrl;
  //             } 
  //             else {
  //               const defaultCountry = prevCountry && countries.includes(prevCountry)
  //                 ? prevCountry
  //                 : countries[0];
  //               // Mark that we need to navigate if no/invalid country in URL
  //               if ((!countryParam || !validCountryFromUrl) && defaultCountry) {
  //                 shouldNavigateRef.current = true;
  //               }
  //               return defaultCountry;
  //             }
  //           }
  //           return prevCountry;
  //         });
  //       }
  //     } catch (error) {
  //   //     }
  //   };

  //   fetchCountries();
  // }, [selectedYear, countryParam, navigate]);

// Fetch available countries when year changes
useEffect(() => {
  if (!selectedYear) return;

  const fetchCountries = async () => {
    try {
      const response = await fetch(`${API_BASE}/data-glance/countries?year=${selectedYear}`);
      if (response.ok) {
        const data = await response.json();
        const countries = (data.countries || []).sort();
        setAvailableCountries(countries);

        const countryFromUrl = countryParam ? urlToCountry(countryParam, countries) : null;
        const validCountryFromUrl = countryFromUrl && countries.includes(countryFromUrl);

        // ✅ Prevent auto-reset if user clicked an unknown country
        setSelectedCountry(prevCountry => {
          if (countries.length > 0) {
            // If the user clicked a country that’s not in availableCountries, keep it
            if (prevCountry && !countries.includes(prevCountry)) {
              return prevCountry; // 👈 Don’t reset — stay on it
            }

            // Otherwise follow the existing logic
            if (validCountryFromUrl) {
              return countryFromUrl;
            } else {
              const defaultCountry =
                prevCountry && countries.includes(prevCountry)
                  ? prevCountry
                  : countries[0];
              if ((!countryParam || !validCountryFromUrl) && defaultCountry) {
                shouldNavigateRef.current = true;
              }
              return defaultCountry;
            }
          }
          return prevCountry;
        });
      }
    } catch (error) {
    }
  };

  fetchCountries();
}, [selectedYear, countryParam, navigate]);

  // Sync URL with selected country when it changes (initial load and updates)
  useEffect(() => {
    if (selectedCountry && availableCountries.length > 0 && availableCountries.includes(selectedCountry)) {
      const urlFormatCountry = countryToUrl(selectedCountry);
      // Update URL if country is different from URL param or if we marked that navigation is needed
      if (countryParam !== urlFormatCountry || shouldNavigateRef.current) {
        shouldNavigateRef.current = false; // Reset the flag
        navigate(`/country-energy-profile/${urlFormatCountry}`, { replace: true });
      }
    }
  }, [selectedCountry, availableCountries, countryParam, navigate]);

  // Helper function to normalize country names for API calls
  // Only use this for countries that have different names in the database
  const normalizeCountryNameForAPI = (countryName) => {
    if (!countryName) return countryName;

    // Country name mappings for API compatibility
    // Only add mappings here if the database uses a different name
    const countryNameMap = {
      "Democratic Republic of the Congo": "Congo, Dem. Rep.",
      "Congo": "Congo, Rep.",
      // Note: "Côte d'Ivoire" is stored as-is in the database, so no mapping needed
      // Add more mappings as needed
    };

    // Check if there's a direct mapping
    if (countryNameMap[countryName]) {
      return countryNameMap[countryName];
    }

    // Return original name if no mapping found
    return countryName;
  };

  // Fetch country data when country or year changes
  useEffect(() => {
    if (!selectedCountry || !selectedYear) return;

    const fetchCountryData = async () => {
      try {
        // Only show loader on initial page load
        if (isInitialLoadRef.current) {
        setLoading(true);
        }
        const normalizedCountryName = normalizeCountryNameForAPI(selectedCountry);
        // console.log('Selected Country:', selectedCountry);
        // console.log('Normalized Country for API:', normalizedCountryName);
        // console.log('Encoded Country:', encodeURIComponent(normalizedCountryName));

        // Optimized: Fetch region and main data in parallel
        const [regionResponse, mainDataResponse] = await Promise.all([
          fetch(
          `${API_BASE}/country-energy-profile/region?country=${encodeURIComponent(normalizedCountryName)}`
          ),
          fetch(
            `${API_BASE}/data-glance-region-and-country-wise?year=${selectedYear}&country=${encodeURIComponent(normalizedCountryName)}`
          )
        ]);

        // Optimized: Parse JSON responses in parallel
        const [regionDataResult, mainDataResult] = await Promise.all([
          regionResponse.ok 
            ? regionResponse.json().catch(() => ({ success: false, regions: [] }))
            : Promise.resolve({ success: false, regions: [] }),
          mainDataResponse.ok
            ? mainDataResponse.json().catch(() => [])
            : Promise.resolve([])
        ]);

        // Process region response
        if (regionDataResult.success && regionDataResult.regions && Array.isArray(regionDataResult.regions) && regionDataResult.regions.length > 0) {
          setCountryRegion(regionDataResult.regions[0]);
        } else {
          setCountryRegion(null);
        }

        if (mainDataResult && Array.isArray(mainDataResult) && mainDataResult.length > 0) {
          const countryInfo = mainDataResult[0];
          // console.log('API Response data:', mainDataResult);

            // Fetch indicators for the country
            const indicatorsResponse = await fetch(
              `${API_BASE}/country-indicators?country=${encodeURIComponent(normalizedCountryName)}&year=${selectedYear}`
            );

            let indicators = {};
            if (indicatorsResponse.ok) {
              const indicatorsData = await indicatorsResponse.json();
              indicators = indicatorsData || {};
            }

            // Update country data in one go to trigger smooth animation
            setCountryData({
              population: countryInfo.population || null,
              populationAccessNational: indicators.populationAccessNational || null,
              populationAccessRural: indicators.populationAccessRural || countryInfo.electricity_access || null,
              populationAccessUrban: indicators.populationAccessUrban || null,
              cleanCookingAccess: indicators.cleanCookingAccess || countryInfo.clean_cooking_access || null,
              totalCommitments: indicators.totalCommitments || null,
              totalProjectsCount: indicators.totalProjectsCount || null
            });

            // Use data from countryInfo for electricity import/export
            let electricityImport = countryInfo.electricity_import || null;
            let electricityExport = countryInfo.electricity_export || null;

            // Check if country or year changed to trigger animation
            const countryChanged = previousCountryRef.current !== selectedCountry;
            const yearChanged = previousYearRef.current !== selectedYear;
            
            if (countryChanged || yearChanged) {
              setShouldAnimate(true);
              previousCountryRef.current = selectedCountry;
              previousYearRef.current = selectedYear;
            }

            // Calculate total population by summing all four components (frontend calculation for accuracy)
            const totalPopulation = calculateTotalPopulation(
              countryInfo.population_with_access_urban,
              countryInfo.population_with_access_rural,
              countryInfo.population_without_access_urban,
              countryInfo.population_without_access_rural
            );

            // Update dashboard data
            setDashboardData({
              electricityGeneration: countryInfo.electricity_generation || null,
              electricityImport: electricityImport,
              electricityExport: electricityExport,
              gdp: countryInfo.gdp_per_capita || null,
              cleanCooking: countryInfo.clean_cooking_access || null,
              population: totalPopulation > 0 ? totalPopulation : null,
              populationWithAccessUrban: countryInfo.population_with_access_urban || null,
              populationWithAccessRural: countryInfo.population_with_access_rural || null,
              populationWithoutAccessUrban: countryInfo.population_without_access_urban || null,
              populationWithoutAccessRural: countryInfo.population_without_access_rural || null,
              perCapitaElectricity: countryInfo.electricity_final_consumption_per_capita || null,
              totalCommitments: indicators.totalCommitments || null,
              totalProjectsCount: indicators.totalProjectsCount || null
            });
            // console.log('Dashboard Data:', dashboardData);
            // console.log('Total Commitments:', dashboardData.totalCommitments);
          } else {
          
            // ✅ Reset all states so UI clears old data
            setCountryRegion(null);
            setCountryData(null);
            setDashboardData({
              electricityGeneration: null,
              electricityImport: null,
              electricityExport: null,
              gdp: null,
              cleanCooking: null,
              population: null,
              populationWithAccessUrban: null,
              populationWithAccessRural: null,
              populationWithoutAccessUrban: null,
              populationWithoutAccessRural: null,
              perCapitaElectricity: null,
              totalCommitments: null,
              totalProjectsCount: null
            });
        }
      } catch (error) {
      } finally {
        if (isInitialLoadRef.current) {
        setLoading(false);
          isInitialLoadRef.current = false;
        }
      }
    };

    fetchCountryData();
  }, [selectedCountry, selectedYear]);

  // Initialize tableYear to selectedYear only once (keep it independent after that)
  useEffect(() => {
    if (!selectedCountry || !selectedYear) {
      setTableYear(null);
      return;
    }

    // Only initialize tableYear if it's null (first time or after being cleared)
    // Don't reset it when country changes - keep it independent
    if (!tableYear) {
      setTableYear(selectedYear);
    }
  }, [selectedCountry, selectedYear]);

  // Fetch electricity generation table data
  useEffect(() => {
    if (!selectedCountry || !tableYear) return;

    const fetchElectricityGenerationTableData = async () => {
      try {
        setLoadingElectricityGenerationTable(true);
        const normalizedCountryName = normalizeCountryNameForAPI(selectedCountry);

        const indicators = [
          'Electricity generated from solar thermal energy (GWh, irena)',
          'Electricity generated from solar photovoltaic (GWh, irena)',
          'Electricity generated from renewable sources (GWh, irena)',
          'Electricity generated from onshore wind energy (GWh, irena)',
          'Electricity generated from geothermal energy (GWh, irena)',
          'Electricity generated from  non renewable (GWh, irena) (fossil fuels)'
        ];

        const dataPromises = indicators.map(async (indicator) => {
          try {
            const url = `${API_BASE}/country-indicators?country=${encodeURIComponent(normalizedCountryName)}&year=${tableYear}&indicator=${encodeURIComponent(indicator)}`;
            // console.log(`Fetching indicator: ${indicator}`);
            // console.log(`URL: ${url}`);
            const response = await fetch(url);
            if (response.ok) {
              const result = await response.json();
              // console.log(`Response for ${indicator}:`, result);
              return {
                indicator,
                value: result.value || null
              };
            } else {
              return { indicator, value: null };
            }
          } catch (error) {
            return { indicator, value: null };
          }
        });

        const results = await Promise.all(dataPromises);

        // Map indicators to source names and icons
        const sourceMapping = {
          'Electricity generated from solar thermal energy (GWh, irena)': {
            name: 'Solar Thermal',
            icon: '/images/svg/sources/solar.svg',
            category: 'solar',
            color: '#ffdd00',
            iconClass: 'icon-solar'
          },
          'Electricity generated from solar photovoltaic (GWh, irena)': {
            name: 'Solar Photovoltaic',
            icon: '/images/svg/sources/solar.svg',
            category: 'solar',
            color: '#ffdd00',
            iconClass: 'icon-solar'
          },
          'Electricity generated from renewable sources (GWh, irena)': {
            name: 'Renewable',
            icon: '/images/svg/sources/renewable-energy.svg',
            category: 'other',
            color: '#41750f',
            iconClass: 'icon-renewable'
          },
          'Electricity generated from onshore wind energy (GWh, irena)': {
            name: 'Wind',
            icon: '/images/svg/sources/wind.svg',
            category: 'other',
            color: '#76bed0',
            iconClass: 'icon-wind'
          },
          'Electricity generated from geothermal energy (GWh, irena)': {
            name: 'Geothermal',
            icon: '/images/svg/sources/geothermal-energy.svg',
            category: 'other',
            color: '#ff5959',
            iconClass: 'icon-geothermal'
          },
          'Electricity generated from  non renewable (GWh, irena) (fossil fuels)': {
            name: 'Fossil Fuels',
            icon: '/images/svg/sources/fossil-fuel.svg',
            category: 'other',
            color: '#84703f',
            iconClass: 'icon-fossil-fuel'
          }
        };

        // Process data and group solar types
        const processedData = results
          .map(result => ({
            indicator: result.indicator,
            source: sourceMapping[result.indicator]?.name || result.indicator,
            icon: sourceMapping[result.indicator]?.icon || '/images/svg/sources/fossil-fuel.svg',
            category: sourceMapping[result.indicator]?.category || 'other',
            color: sourceMapping[result.indicator]?.color || '#222831',
            iconClass: sourceMapping[result.indicator]?.iconClass || 'icon-default',
            value: result.value,
            displayOrder: sourceMapping[result.indicator]?.displayOrder || 999
          }))
          .filter(item => item.value !== null && item.value !== undefined);

        // Group solar types
        const solarData = processedData.filter(item => item.category === 'solar');
        const otherData = processedData.filter(item => item.category !== 'solar');

        // Calculate total solar
        const solarTotal = solarData.reduce((sum, item) => {
          const numValue = typeof item.value === 'string' ? parseFloat(item.value) : item.value;
          return sum + (isNaN(numValue) ? 0 : numValue);
        }, 0);

        // Build table data in specific order: Solar, Wind, Renewable, Geothermal, Fossil Fuels
        const tableData = [];

        // 1. Add Solar first (if available)
        if (solarData.length > 0) {
          tableData.push({
            source: 'Solar',
            icon: '/images/svg/sources/solar.svg',
            color: '#ffdd00',
            iconClass: 'icon-solar',
            value: solarTotal,
            isSolar: true,
            isSolarSubItem: false,
            solarSubItems: solarData,
            displayOrder: 1
          });
        }

        // 2. Add Wind
        const windData = otherData.find(item => item.source === 'Wind');
        if (windData) {
          tableData.push({
            ...windData,
            isSolar: false,
            isSolarSubItem: false,
            displayOrder: 2
          });
        }

        // 3. Add Renewable
        const renewableData = otherData.find(item => item.source === 'Renewable');
        if (renewableData) {
          tableData.push({
            ...renewableData,
            isSolar: false,
            isSolarSubItem: false,
            displayOrder: 3
          });
        }

        // 4. Add Geothermal
        const geothermalData = otherData.find(item => item.source === 'Geothermal');
        if (geothermalData) {
          tableData.push({
            ...geothermalData,
            isSolar: false,
            isSolarSubItem: false,
            displayOrder: 4
          });
        }

        // 5. Add Fossil Fuels
        const fossilFuelsData = otherData.find(item => item.source === 'Fossil Fuels');
        if (fossilFuelsData) {
          tableData.push({
            ...fossilFuelsData,
            isSolar: false,
            isSolarSubItem: false,
            displayOrder: 5
          });
        }

        // // 6. Add Non-renewable (using same data as Fossil Fuels but with different name and color)
        // if (fossilFuelsData) {
        //   tableData.push({
        //     source: 'Non-renewable',
        //     icon: '/images/svg/sources/fossil-fuel.svg',
        //     color: '#222831',
        //     iconClass: 'icon-non-renewable',
        //     value: fossilFuelsData.value,
        //     isSolar: false,
        //     isSolarSubItem: false,
        //     displayOrder: 6
        //   });
        // }

        // Sort by display order to ensure correct sequence
        tableData.sort((a, b) => (a.displayOrder || 999) - (b.displayOrder || 999));

        setElectricityGenerationTableData(tableData);
      } catch (error) {
        setElectricityGenerationTableData([]);
      } finally {
        setLoadingElectricityGenerationTable(false);
      }
    };

    fetchElectricityGenerationTableData();
  }, [selectedCountry, tableYear]);

  // Fetch electricity access population data
  useEffect(() => {
    if (!selectedCountry || availableYears.length === 0) return;

    const fetchElectricityAccessData = async () => {
      try {
        setLoadingElectricityData(true);
        const normalizedCountryName = normalizeCountryNameForAPI(selectedCountry);
        const response = await fetch(
          `${API_BASE}/electricity-access-population?country=${encodeURIComponent(normalizedCountryName)}`
        );

        if (response.ok) {
          const result = await response.json();
          const allData = result.data || [];

          // Filter data to only include years that are in availableYears
          const filteredData = allData.filter(item =>
            availableYears.includes(item.year)
          );

          setElectricityAccessData(filteredData);
        }
      } catch (error) {
        setElectricityAccessData([]);
      } finally {
        setLoadingElectricityData(false);
      }
    };

    fetchElectricityAccessData();
  }, [selectedCountry, availableYears]);

  // Fetch clean cooking RISE indicator data
  useEffect(() => {
    if (!selectedCountry || availableYears.length === 0) return;

    const fetchCleanCookingData = async () => {
      try {
        setLoadingCleanCookingData(true);
        const normalizedCountryName = normalizeCountryNameForAPI(selectedCountry);
        const response = await fetch(
          `${API_BASE}/clean-cooking-rise?country=${encodeURIComponent(normalizedCountryName)}`
        );

        if (response.ok) {
          const result = await response.json();
          const allData = result.data || [];

          // Filter data to only include years that are in availableYears
          const filteredData = allData.filter(item =>
            availableYears.includes(item.year)
          );

          setCleanCookingData(filteredData);
        }
      } catch (error) {
        setCleanCookingData([]);
      } finally {
        setLoadingCleanCookingData(false);
      }
    };

    fetchCleanCookingData();
  }, [selectedCountry, availableYears]);

  // Fetch energy efficiency RISE indicator data
  useEffect(() => {
    if (!selectedCountry || availableYears.length === 0) return;

    const fetchEnergyEfficiencyData = async () => {
      try {
        setLoadingEnergyEfficiencyData(true);
        const normalizedCountryName = normalizeCountryNameForAPI(selectedCountry);
        const response = await fetch(
          `${API_BASE}/energy-efficiency-rise?country=${encodeURIComponent(normalizedCountryName)}`
        );

        if (response.ok) {
          const result = await response.json();
          const allData = result.data || [];

          // Filter data to only include years that are in availableYears
          const filteredData = allData.filter(item =>
            availableYears.includes(item.year)
          );

          setEnergyEfficiencyData(filteredData);
        }
      } catch (error) {
        setEnergyEfficiencyData([]);
      } finally {
        setLoadingEnergyEfficiencyData(false);
      }
    };

    fetchEnergyEfficiencyData();
  }, [selectedCountry, availableYears]);

  // Fetch electricity demand consumption data
  useEffect(() => {
    if (!selectedCountry || availableYears.length === 0) return;

    const fetchElectricityDemandData = async () => {
      try {
        setLoadingElectricityDemandData(true);
        const normalizedCountryName = normalizeCountryNameForAPI(selectedCountry);
        const response = await fetch(
          `${API_BASE}/electricity-demand-consumption?country=${encodeURIComponent(normalizedCountryName)}`
        );

        if (response.ok) {
          const result = await response.json();
          const allData = result.data || [];

          // Filter data to only include years that are in availableYears
          const filteredData = allData.filter(item =>
            availableYears.includes(item.year)
          );

          setElectricityDemandData(filteredData);
        }
      } catch (error) {
        setElectricityDemandData([]);
      } finally {
        setLoadingElectricityDemandData(false);
      }
    };

    fetchElectricityDemandData();
  }, [selectedCountry, availableYears]);

  // Fetch energy intensity data
  useEffect(() => {
    if (!selectedCountry || availableYears.length === 0) return;

    const fetchEnergyIntensityData = async () => {
      try {
        setLoadingEnergyIntensityData(true);
        const normalizedCountryName = normalizeCountryNameForAPI(selectedCountry);
        const url = `${API_BASE}/energy-intensity?country=${encodeURIComponent(normalizedCountryName)}`;
        // console.log('Fetching energy intensity data from:', url);
        const response = await fetch(url);

        if (response.ok) {
          const result = await response.json();
          // console.log('Energy intensity API response:', result);
          const allData = result.data || [];
          // console.log('Energy intensity all data:', allData);

          // Filter data to only include years that are in availableYears
          const filteredData = allData.filter(item =>
            availableYears.includes(item.year)
          );
          // console.log('Energy intensity filtered data:', filteredData);

          setEnergyIntensityData(filteredData);
        } else {
          const errorText = await response.text();
          setEnergyIntensityData([]);
        }
      } catch (error) {
        setEnergyIntensityData([]);
      } finally {
        setLoadingEnergyIntensityData(false);
      }
    };

    fetchEnergyIntensityData();
  }, [selectedCountry, availableYears]);

  // Fetch electricity installed capacity data
  useEffect(() => {
    if (!selectedCountry || availableYears.length === 0) return;

    const fetchElectricityCapacityData = async () => {
      try {
        setLoadingElectricityCapacityData(true);
        const normalizedCountryName = normalizeCountryNameForAPI(selectedCountry);
        const response = await fetch(
          `${API_BASE}/electricity-installed-capacity?country=${encodeURIComponent(normalizedCountryName)}`
        );

        if (response.ok) {
          const result = await response.json();
          const allData = result.data || [];

          // Filter data to only include years that are in availableYears
          const filteredData = allData.filter(item =>
            availableYears.includes(item.year)
          );

          setElectricityCapacityData(filteredData);
        }
      } catch (error) {
        setElectricityCapacityData([]);
      } finally {
        setLoadingElectricityCapacityData(false);
      }
    };

    fetchElectricityCapacityData();
  }, [selectedCountry, availableYears]);


  // Fetch renewable energy generation mix data
  useEffect(() => {
    if (!selectedCountry || availableYears.length === 0) return;

    const fetchRenewableEnergyGenerationMixData = async () => {
      try {
        setLoadingRenewableEnergyGenerationMixData(true);
        const normalizedCountryName = normalizeCountryNameForAPI(selectedCountry);
        const response = await fetch(
          `${API_BASE}/renewable-energy-generation-mix?country=${encodeURIComponent(normalizedCountryName)}`
        );

        if (response.ok) {
          const result = await response.json();
          const allData = result.data || [];

          // Filter data to only include years that are in availableYears
          const filteredData = allData.filter(item =>
            availableYears.includes(item.year)
          );

          setRenewableEnergyGenerationMixData(filteredData);
        }
      } catch (error) {
        setRenewableEnergyGenerationMixData([]);
      } finally {
        setLoadingRenewableEnergyGenerationMixData(false);
      }
    };

    fetchRenewableEnergyGenerationMixData();

  }, [selectedCountry, availableYears]);


  // Helper function to format numbers with commas
  const formatNumber = (value, isPercentage = false) => {
    if (value === null || value === undefined || value === 'N/A') {
      return 'N/A';
    }

    const numValue = typeof value === 'string' ? parseFloat(value) : value;

    if (isNaN(numValue)) {
      return 'N/A';
    }

    if (isPercentage) {
      return numValue.toFixed(1);
    }

    // Format with commas for large numbers
    return numValue.toLocaleString('en-US', {
      maximumFractionDigits: 1,
      minimumFractionDigits: 0
    });
  };

  // Helper function to format GDP in millions
  const formatGDP = (value) => {
    if (value === null || value === undefined || value === 'N/A') {
      return 'N/A';
    }

    const numValue = typeof value === 'string' ? parseFloat(value) : value;

    if (isNaN(numValue)) {
      return 'N/A';
    }

    // Convert to millions (divide by 1,000,000)
    const millionValue = numValue / 1000000;

    return millionValue.toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
  };

  // Helper function to calculate total population from four indicators
  const calculateTotalPopulation = (withAccessUrban, withAccessRural, withoutAccessUrban, withoutAccessRural) => {
    // Helper to parse a value (handles 'N/A', null, undefined, strings, numbers)
    const parseValue = (val) => {
      if (val === null || val === undefined || val === '' || val === 'N/A') {
        return 0;
      }
      const numValue = typeof val === 'string' ? parseFloat(val) : Number(val);
      return isNaN(numValue) ? 0 : numValue;
    };

    // Sum all four components
    const total = parseValue(withAccessUrban) + 
                  parseValue(withAccessRural) + 
                  parseValue(withoutAccessUrban) + 
                  parseValue(withoutAccessRural);
    
    return total;
  };

  // Format population number
  const formatPopulation = (value) => {
    if (!value || value === 'N/A') return 'N/A';
    const num = parseFloat(value);
    if (isNaN(num)) return 'N/A';
    return num.toLocaleString('en-US', { maximumFractionDigits: 1 });
  };

  // Format percentage
  const formatPercentage = (value) => {
    if (!value || value === 'N/A') return null;
    const num = parseFloat(value);
    if (isNaN(num)) return null;
    return num.toFixed(1);
  };

  // Format currency (for investments) - similar to ImpactInsightsPage
  const formatCurrency = (amount) => {
    if (amount === null || amount === undefined || isNaN(amount)) return '$0';
    if (amount >= 1000000000) {
      return '$' + (amount / 1000000000).toFixed(1) + 'B';
    } else if (amount >= 1000000) {
      return '$' + (amount / 1000000).toFixed(1) + 'M';
    } else if (amount >= 1000) {
      return '$' + (amount / 1000).toFixed(1) + 'K';
    }
    return '$' + Math.round(amount).toLocaleString();
  };

  // Animation speed in milliseconds (lower = faster)
  const animationSpeed = 15;

  // Use animated numbers for all data card values
  const animatedElectricityGeneration = useAnimatedNumber(
    dashboardData.electricityGeneration ? formatNumber(dashboardData.electricityGeneration) : 'N/A',
    shouldAnimate && !loading,
    animationSpeed
  );
  const animatedTotalCommitments = useAnimatedNumber(
    dashboardData.totalCommitments ? formatCurrency(dashboardData.totalCommitments) : '$0',
    shouldAnimate && !loading,
    animationSpeed
  );
  const animatedTotalProjectsCount = useAnimatedNumber(
    dashboardData.totalProjectsCount ? formatNumber(dashboardData.totalProjectsCount) : 'N/A',
    shouldAnimate && !loading,
    animationSpeed
  );
  const animatedGDP = useAnimatedNumber(
    dashboardData.gdp ? formatGDP(dashboardData.gdp) : 'N/A',
    shouldAnimate && !loading,
    animationSpeed
  );
  const animatedPopulation = useAnimatedNumber(
    dashboardData.population ? formatPopulation(dashboardData.population) : 'N/A',
    shouldAnimate && !loading,
    animationSpeed
  );
  const animatedPopulationWithAccessUrban = useAnimatedNumber(
    dashboardData.populationWithAccessUrban ? formatPopulation(dashboardData.populationWithAccessUrban) : 'N/A',
    shouldAnimate && !loading,
    animationSpeed
  );
  const animatedPopulationWithAccessRural = useAnimatedNumber(
    dashboardData.populationWithAccessRural ? formatPopulation(dashboardData.populationWithAccessRural) : 'N/A',
    shouldAnimate && !loading,
    animationSpeed
  );
  const animatedPopulationWithoutAccessUrban = useAnimatedNumber(
    dashboardData.populationWithoutAccessUrban ? formatPopulation(dashboardData.populationWithoutAccessUrban) : 'N/A',
    shouldAnimate && !loading,
    animationSpeed
  );
  const animatedPopulationWithoutAccessRural = useAnimatedNumber(
    dashboardData.populationWithoutAccessRural ? formatPopulation(dashboardData.populationWithoutAccessRural) : 'N/A',
    shouldAnimate && !loading,
    animationSpeed
  );
  const animatedElectricityImport = useAnimatedNumber(
    dashboardData.electricityImport ? formatNumber(dashboardData.electricityImport) : 'N/A',
    shouldAnimate && !loading,
    animationSpeed
  );
  const animatedElectricityExport = useAnimatedNumber(
    dashboardData.electricityExport ? formatNumber(dashboardData.electricityExport) : 'N/A',
    shouldAnimate && !loading,
    animationSpeed
  );
  const animatedCleanCooking = useAnimatedNumber(
    dashboardData.cleanCooking ? formatNumber(dashboardData.cleanCooking, true) : 'N/A',
    shouldAnimate && !loading,
    animationSpeed
  );
  const animatedPerCapitaElectricity = useAnimatedNumber(
    dashboardData.perCapitaElectricity ? formatNumber(dashboardData.perCapitaElectricity) : 'N/A',
    shouldAnimate && !loading,
    animationSpeed
  );

  // Helper function to calculate dataZoom range (show last 6 items)
  const getDataZoomRange = (yearsArray) => {
    if (!yearsArray || yearsArray.length === 0) return [0,100];
    // Show last 6 items
    const startIndex = Math.max(0, yearsArray.length - 6);
    const endIndex = yearsArray.length - 1;
    const startPercent = yearsArray.length > 1 ? (startIndex / (yearsArray.length - 1)) * 100 : 0;
    const endPercent = yearsArray.length > 1 ? (endIndex / (yearsArray.length - 1)) * 100 : 100;
    return [startPercent, endPercent];
  };

  // ECharts options for "Population with access to electricity" chart
  const withAccessChartOption = useMemo(() => {
    if (!electricityAccessData || electricityAccessData.length === 0) {
      return {};
    }

    const years = electricityAccessData.map(item => item.year.toString());
    // Calculate national data by summing urban + rural (frontend calculation for accuracy)
    const nationalData = electricityAccessData.map(item => {
      const urban = item.withAccessUrban || 0;
      const rural = item.withAccessRural || 0;
      const total = urban + rural;
      return total > 0 ? total : null;
    });
    const ruralData = electricityAccessData.map(item => item.withAccessRural || null);
    const urbanData = electricityAccessData.map(item => item.withAccessUrban || null);

    return {
      tooltip: {
        trigger: 'axis',
        axisPointer: {
          type: 'shadow'
        },
        formatter: function (params) {
          if (!params || !Array.isArray(params) || params.length === 0) {
            return '';
          }
          const year = params[0].axisValue || params[0].name || '';
          let items = '';
          params.forEach(function (item, index) {
            if (item) {
              const val = item.value;
              const value = (val !== null && val !== undefined && !isNaN(val)) ? val.toFixed(2) + 'M' : 'N/A';
              const isLast = index === params.length - 1;
              const borderStyle = isLast ? 'border-top: 1px solid #eee; padding-top: 4px; margin-top: 4px;' : '';
              items += '<div style="color: #666; font-size:14px; font-weight:500; margin-bottom: 4px; font-family: \'Poppins\', sans-serif; ' + borderStyle + '">' +
                '<span style="color:' + (item.color || '#666') + '">●</span> ' +
                (item.seriesName || '') + ': <span style="font-weight: 600; color: #333;">' + value + '</span>' +
                '</div>';
            }
          });
          return '<div style="padding: 5px; background: white; border-radius: 8px; font-family: \'Poppins\', sans-serif;">' +
            '<div style="font-size: 14px; font-weight: 500; color: #333; margin-bottom: 8px; font-family: \'Poppins\', sans-serif;">' +
            year + '</div>' + items + '</div>';
        },
        backgroundColor: 'rgba(255, 255, 255, 0.98)',
        borderColor: '#e5e5e5',
        borderWidth: 1,
        borderRadius: 8,
        textStyle: {
          fontSize: 14,
          color: '#666666',
          fontWeight: '500',
          fontFamily: "'Poppins', sans-serif"
        }
      },
      legend: {
        data: ['National', 'Rural', 'Urban'],
        bottom: 50,
        left: 'center',
        itemWidth: 12,
        itemHeight: 12,
        itemGap: 24,
        textStyle: {
          fontFamily: "'Poppins', sans-serif",
          fontSize: 14,
          color: '#181818',
          fontWeight: 'normal'
        },
        icon: 'circle'
      },
      grid: {
        left: '8%', // Increased to ensure yAxis label is always visible
        right: '8%', // Increased to ensure yAxis label is always visible
        bottom: '30%',
        top: '15%',
        containLabel: true
      },
      dataZoom: [
        {
          type: 'slider',
          show: true,
          xAxisIndex: [0],
          start: getDataZoomRange(years)[0],
          end: getDataZoomRange(years)[4],
          bottom: 15,
          height: 20,
          backgroundColor: '#f8f9fa',
          borderColor: '#e9ecef',
          fillerColor: 'rgba(135, 206, 250, 0.3)',
          selectedDataBackground: {
            areaStyle: {
              color: 'rgba(135, 206, 250, 0.2)'
            },
            lineStyle: {
              color: '#87ceeb',
              width: 1
            }
          },
          handleStyle: {
            color: '#87ceeb',
            borderColor: '#87ceeb',
            borderWidth: 1,
            shadowBlur: 2,
            shadowColor: 'rgba(0, 0, 0, 0.1)'
          },
          textStyle: {
            color: '#666',
            fontSize: 14,
            fontFamily: "'Poppins', sans-serif"
          },
          showDetail: false,
          showDataShadow: false,
          realtime: true,
          filterMode: 'filter'
        },
        {
          type: 'inside',
          xAxisIndex: [0],
          start: getDataZoomRange(years)[0],
          end: getDataZoomRange(years)[1],
          zoomLock: true,
          zoomOnMouseWheel: false,
          moveOnMouseMove: false,
          moveOnMouseWheel: false
        }
      ],
      xAxis: {
        type: 'category',
        data: years,
        axisLine: {
          lineStyle: {
            color: '#e5e5e5'
          }
        },
        axisTick: {
          show: false
        },
        axisLabel: {
          color: '#666',
          fontSize: 14,
          fontFamily: '"Poppins", sans-serif'
        }
      },
      yAxis: {
        type: 'value',
        name: 'Population (Million)',
        nameLocation: 'middle',
        nameGap: 50,
        axisLine: {
          lineStyle: {
            color: '#e5e5e5'
          }
        },
        axisTick: {
          show: false
        },
        axisLabel: {
          color: '#666',
          fontSize: 14,
          fontFamily: '"Poppins", sans-serif',
          show: true
        },
        nameTextStyle: {
          color: '#666',
          fontSize: 14,
          fontFamily: '"Poppins", sans-serif',
          padding: [0, 0, 0, 0]
        },
        splitLine: {
          lineStyle: {
            color: '#f0f0f0',
            type: 'dashed'
          }
        }
      },
      series: [
        {
          name: 'National',
          type: 'bar',
          data: nationalData,
          itemStyle: {
            color: '#7386a8'
          },
          barWidth: '25%'
        },
        {
          name: 'Rural',
          type: 'bar',
          data: ruralData,
          itemStyle: {
            color: '#BAD7E9'
          },
          barWidth: '25%'
        },
        {
          name: 'Urban',
          type: 'bar',
          data: urbanData,
          itemStyle: {
            color: '#2B3467'
          },
          barWidth: '25%'
        }
      ],
      media: [
        {
          query: { maxWidth: 1024 }, // Tablet and below
          option: {
            grid: {
              left: '8%', // Increased to ensure yAxis label is always visible
              right: '8%', // Increased to ensure yAxis label is always visible
              top: '12%',
              bottom: '25%'
            },
            legend: {
              bottom: 40,
              itemWidth: 10,
              itemHeight: 10,
              textStyle: { fontSize: 12 }
            },
            xAxis: {
              axisLabel: { fontSize: 12 }
            },
            yAxis: {
              name: 'Population (Million)',
              axisLabel: { fontSize: 12, show: true },
              nameTextStyle: { fontSize: 12, padding: [0, 0, 0, 0] }
            }
          }
        },
        {
          query: { maxWidth: 768 }, // Mobile
          option: {
            grid: {
              left: '12%', // Increased to ensure yAxis label is always visible on mobile
              right: '8%', // Increased to ensure yAxis label is always visible on mobile
              bottom: '25%'
            },
            legend: {
              show: true,
              bottom: 40,
              textStyle: { fontSize: 11 },
            },
            xAxis: {
              axisLabel: { fontSize: 11 }
            },
            yAxis: {
              name: 'Population (Million)',
              nameGap: 35, // Reduced gap for mobile
              axisLabel: { fontSize: 11, show: true },
              nameTextStyle: { fontSize: 11, padding: [0, 0, 0, 0] }
            }
          }
        }
      ]
    };
  }, [electricityAccessData]);

  // ECharts options for "Population without access to electricity" chart
  const withoutAccessChartOption = useMemo(() => {
    if (!electricityAccessData || electricityAccessData.length === 0) {
      return {};
    }

    const years = electricityAccessData.map(item => item.year.toString());
    // Calculate national data by summing urban + rural (frontend calculation for accuracy)
    const nationalData = electricityAccessData.map(item => {
      const urban = item.withoutAccessUrban || 0;
      const rural = item.withoutAccessRural || 0;
      const total = urban + rural;
      return total > 0 ? total : null;
    });
    const ruralData = electricityAccessData.map(item => item.withoutAccessRural || null);
    const urbanData = electricityAccessData.map(item => item.withoutAccessUrban || null);

    return {
      tooltip: {
        trigger: 'axis',
        axisPointer: {
          type: 'shadow'
        },
        formatter: function (params) {
          if (!params || !Array.isArray(params) || params.length === 0) {
            return '';
          }
          const year = params[0].axisValue || params[0].name || '';
          let items = '';
          params.forEach(function (item, index) {
            if (item) {
              const val = item.value;
              const value = (val !== null && val !== undefined && !isNaN(val)) ? val.toFixed(2) + 'M' : 'N/A';
              const isLast = index === params.length - 1;
              const borderStyle = isLast ? 'border-top: 1px solid #eee; padding-top: 4px; margin-top: 4px;' : '';
              items += '<div style="color: #666; margin-bottom: 4px; font-family: \'Poppins\', sans-serif; ' + borderStyle + '">' +
                '<span style="color:' + (item.color || '#666') + '">●</span> ' +
                (item.seriesName || '') + ': <span style="font-weight: 600; color: #333;">' + value + '</span>' +
                '</div>';
            }
          });
          return '<div style="padding: 5px; background: white; border-radius: 8px; font-family: \'Poppins\', sans-serif;">' +
            '<div style="font-size: 14px; font-weight: 500; color: #333; margin-bottom: 8px; font-family: \'Poppins\', sans-serif;">' +
            year + '</div>' + items + '</div>';
        },
        backgroundColor: 'rgba(255, 255, 255, 0.98)',
        borderColor: '#e5e5e5',
        borderWidth: 1,
        borderRadius: 8,
        textStyle: {
          fontFamily: "'Poppins', sans-serif",
          fontSize: 12
        }
      },
      legend: {
        data: ['National', 'Rural', 'Urban'],
        bottom: 50,
        left: 'center',
        itemWidth: 12,
        itemHeight: 12,
        itemGap: 24,
        textStyle: {
          fontFamily: "'Poppins', sans-serif",
          fontSize: 14,
          color: '#181818',
          fontWeight: 'normal'
        },
        icon: 'circle'
      },
      grid: {
        left: '8%', // Increased to ensure yAxis label is always visible
        right: '8%', // Increased to ensure yAxis label is always visible
        bottom: '30%',
        top: '15%',
        containLabel: true
      },
      dataZoom: [
        {
          type: 'slider',
          show: true,
          xAxisIndex: [0],
          start: getDataZoomRange(years)[0],
          end: getDataZoomRange(years)[1],
          bottom: 15,
          height: 20,
          backgroundColor: '#f8f9fa',
          borderColor: '#e9ecef',
          fillerColor: 'rgba(135, 206, 250, 0.3)',
          selectedDataBackground: {
            areaStyle: {
              color: 'rgba(135, 206, 250, 0.2)'
            },
            lineStyle: {
              color: '#87ceeb',
              width: 1
            }
          },
          handleStyle: {
            color: '#87ceeb',
            borderColor: '#87ceeb',
            borderWidth: 1,
            shadowBlur: 2,
            shadowColor: 'rgba(0, 0, 0, 0.1)'
          },
          textStyle: {
            color: '#666',
            fontSize: 14,
            fontFamily: "'Poppins', sans-serif"
          },
          showDetail: false,
          showDataShadow: false,
          realtime: true,
          filterMode: 'filter'
        },
        {
          type: 'inside',
          xAxisIndex: [0],
          start: getDataZoomRange(years)[0],
          end: getDataZoomRange(years)[1],
          zoomLock: true,
          zoomOnMouseWheel: false,
          moveOnMouseMove: false,
          moveOnMouseWheel: false
        }
      ],
      xAxis: {
        type: 'category',
        data: years,
        axisLine: {
          lineStyle: {
            color: '#e5e5e5'
          }
        },
        axisTick: {
          show: false
        },
        axisLabel: {
          color: '#666',
          fontSize: 14,
          fontFamily: '"Poppins", sans-serif'
        }
      },
      yAxis: {
        type: 'value',
        name: 'Population (Million)',
        nameLocation: 'middle',
        nameGap: 50,
        axisLine: {
          lineStyle: {
            color: '#e5e5e5'
          }
        },
        axisTick: {
          show: false
        },
        axisLabel: {
          color: '#666',
          fontSize: 14,
          fontFamily: '"Poppins", sans-serif',
          show: true
        },
        nameTextStyle: {
          color: '#666',
          fontSize: 14,
          fontFamily: '"Poppins", sans-serif',
          padding: [0, 0, 0, 0]
        },
        splitLine: {
          lineStyle: {
            color: '#f0f0f0',
            type: 'dashed'
          }
        }
      },
      series: [
        {
          name: 'National',
          type: 'bar',
          data: nationalData,
          itemStyle: {
            color: '#7386a8'
          },
          barWidth: '25%'
        },
        {
          name: 'Rural',
          type: 'bar',
          data: ruralData,
          itemStyle: {
            color: '#BAD7E9'
          },
          barWidth: '25%'
        },
        {
          name: 'Urban',
          type: 'bar',
          data: urbanData,
          itemStyle: {
            color: '#2B3467'
          },
          barWidth: '25%'
        }
      ],
      media: [
        {
          query: { maxWidth: 1024 }, // Tablet and below
          option: {
            grid: {
              left: '8%', // Increased to ensure yAxis label is always visible
              right: '8%', // Increased to ensure yAxis label is always visible
              top: '12%',
              bottom: '25%'
            },
            legend: {
              bottom: 40,
              itemWidth: 10,
              itemHeight: 10,
              textStyle: { fontSize: 12 }
            },
            xAxis: {
              axisLabel: { fontSize: 12 }
            },
            yAxis: {
              name: 'Population (Million)',
              axisLabel: { fontSize: 12, show: true },
              nameTextStyle: { fontSize: 12, padding: [0, 0, 0, 0] }
            }
          }
        },
        {
          query: { maxWidth: 768 }, // Mobile
          option: {
            grid: {
              left: '12%', // Increased to ensure yAxis label is always visible on mobile
              right: '8%', // Increased to ensure yAxis label is always visible on mobile
              bottom: '25%'
            },
            legend: {
              show: true,
              bottom: 40,
              textStyle: { fontSize: 11 },
            },
            xAxis: {
              axisLabel: { fontSize: 11 }
            },
            yAxis: {
              name: 'Population (Million)',
              nameGap: 36, // Reduced gap for mobile
              axisLabel: { fontSize: 11, show: true },
              nameTextStyle: { fontSize: 11, padding: [0, 0, 0, 0] }
            }
          }
        }
      ]
    };
  }, [electricityAccessData]);

  // ECharts options for modal view (larger fonts)
  const withAccessChartOptionModal = useMemo(() => {
    if (!withAccessChartOption || Object.keys(withAccessChartOption).length === 0) {
      return {};
    }
    return {
      ...withAccessChartOption,
      tooltip: {
        ...withAccessChartOption.tooltip,
        textStyle: {
          fontFamily: '"Poppins", sans-serif',
          fontSize: 14
        }
      },
      legend: {
        ...withAccessChartOption.legend,
        bottom: 50,
        textStyle: {
          fontFamily: '"Poppins", sans-serif',
          fontSize: 14
        }
      },
      grid: {
        left: '8%', // Increased to ensure yAxis label is always visible
        right: '8%', // Increased to ensure yAxis label is always visible
        bottom: '20%',
        top: '10%',
        containLabel: true
      },
      xAxis: {
        ...withAccessChartOption.xAxis,
        axisLabel: {
          ...withAccessChartOption.xAxis.axisLabel,
          fontSize: 14,
          fontFamily: '"Poppins", sans-serif'
        }
      },
      yAxis: {
        ...withAccessChartOption.yAxis,
        axisLabel: {
          ...withAccessChartOption.yAxis.axisLabel,
          fontSize: 14,
          fontFamily: '"Poppins", sans-serif'
        },
        nameTextStyle: {
          ...withAccessChartOption.yAxis.nameTextStyle,
          fontSize: 14,
          fontFamily: '"Poppins", sans-serif'
        }
      }
    };
  }, [withAccessChartOption]);

  const withoutAccessChartOptionModal = useMemo(() => {
    if (!withoutAccessChartOption || Object.keys(withoutAccessChartOption).length === 0) {
      return {};
    }
    return {
      ...withoutAccessChartOption,
      tooltip: {
        ...withoutAccessChartOption.tooltip,
        textStyle: {
          fontFamily: '"Poppins", sans-serif',
          fontSize: 14
        }
      },
      legend: {
        ...withoutAccessChartOption.legend,
        bottom: 50,
        textStyle: {
          fontFamily: '"Poppins", sans-serif',
          fontSize: 14
        }
      },
      grid: {
        left: '8%', // Increased to ensure yAxis label is always visible
        right: '8%', // Increased to ensure yAxis label is always visible
        bottom: '30%',
        top: '15%',
        containLabel: true
      },
      xAxis: {
        ...withoutAccessChartOption.xAxis,
        axisLabel: {
          ...withoutAccessChartOption.xAxis.axisLabel,
          fontSize: 14
        }
      },
      yAxis: {
        ...withoutAccessChartOption.yAxis,
        axisLabel: {
          ...withoutAccessChartOption.yAxis.axisLabel,
          fontSize: 14
        },
        nameTextStyle: {
          ...withoutAccessChartOption.yAxis.nameTextStyle,
          fontSize: 14
        }
      }
    };
  }, [withoutAccessChartOption]);

    // Check if all values in both electricity access charts are N/A
  const allElectricityAccessValuesAreNA = useMemo(() => {
    if (!electricityAccessData || electricityAccessData.length === 0) {
      return true;
    }

    // Helper function to check if all values in an array are invalid (null, undefined, or NaN)
    const allValuesInvalid = (dataArray) => {
      if (!dataArray || dataArray.length === 0) return true;
      return dataArray.every(val => val === null || val === undefined || isNaN(val));
    };

    // Check "with access" chart data (using calculated national = urban + rural)
    const withAccessNationalData = electricityAccessData.map(item => {
      const urban = item.withAccessUrban || 0;
      const rural = item.withAccessRural || 0;
      const total = urban + rural;
      return total > 0 ? total : null;
    });
    const withAccessRuralData = electricityAccessData.map(item => item.withAccessRural || null);
    const withAccessUrbanData = electricityAccessData.map(item => item.withAccessUrban || null);
    
    const allWithAccessInvalid = 
      allValuesInvalid(withAccessNationalData) &&
      allValuesInvalid(withAccessRuralData) &&
      allValuesInvalid(withAccessUrbanData);

    // Check "without access" chart data (using calculated national = urban + rural)
    const withoutAccessNationalData = electricityAccessData.map(item => {
      const urban = item.withoutAccessUrban || 0;
      const rural = item.withoutAccessRural || 0;
      const total = urban + rural;
      return total > 0 ? total : null;
    });
    const withoutAccessRuralData = electricityAccessData.map(item => item.withoutAccessRural || null);
    const withoutAccessUrbanData = electricityAccessData.map(item => item.withoutAccessUrban || null);
    
    const allWithoutAccessInvalid = 
      allValuesInvalid(withoutAccessNationalData) &&
      allValuesInvalid(withoutAccessRuralData) &&
      allValuesInvalid(withoutAccessUrbanData);

    // Return true only if ALL values in BOTH charts are invalid
    return allWithAccessInvalid && allWithoutAccessInvalid;
  }, [electricityAccessData]);

  // ECharts options for Clean Cooking RISE indicator line chart
  const cleanCookingChartOption = useMemo(() => {
    if (!cleanCookingData || cleanCookingData.length === 0) {
      return {};
    }

    const years = cleanCookingData.map(item => item.year.toString());
    const scores = cleanCookingData.map(item => {
      const score = item.score;
      // Filter out non-numeric values (like "-")
      if (score === null || score === undefined || score === '-' || isNaN(parseFloat(score))) {
        return null;
      }
      return parseFloat(score);
    });

    return {
      tooltip: {
        trigger: 'axis',
        axisPointer: {
          type: 'line'
        },
        formatter: function (params) {
          if (!params || !Array.isArray(params) || params.length === 0) {
            return '';
          }
          const param = params[0];
          const val = param.value;
          const value = (val !== null && val !== undefined && !isNaN(val)) ? val.toFixed(2) : 'N/A';
          const year = param.axisValue || param.name || '';
          return '<div style="padding: 5px; background: white; border-radius: 8px; font-family: \'Poppins\', sans-serif;">' +
            '<div style="font-size: 14px; font-weight: 500; color: #333; margin-bottom: 8px; font-family: \'Poppins\', sans-serif;">' +
            year + '</div>' +
            '<div style="color: #666; font-family: \'Poppins\', sans-serif;">' +
            '<span style="color:' + (param.color || '#1ABC9C') + '">●</span> ' +
            'Score: <span style="font-weight: 600; color: #333;">' + value + '</span>' +
            '</div>' +
            '</div>';
        },
        backgroundColor: 'rgba(255, 255, 255, 0.98)',
        borderColor: '#e5e5e5',
        borderWidth: 1,
        borderRadius: 8,
        textStyle: {
          fontFamily: "'Poppins', sans-serif",
          fontSize: 14,
          color: '#666666',
          fontWeight: '500'
        }
      },
      legend: {
        data: ['Evolution of total score of RISE indicator'],
        bottom: 50,
        left: 'center',
        itemWidth: 12,
        itemHeight: 12,
        itemGap: 8,
        textStyle: {
          fontFamily: "'Poppins', sans-serif",
          fontSize: 14,
          color: '#181818',
          fontWeight: 'normal'
        },
        icon: 'circle'
      },
      grid: {
        left: '3%',
        right: '4%',
        bottom: '30%',
        top: '10%',
        containLabel: true
      },
      dataZoom: [
        {
          type: 'slider',
          show: true,
          xAxisIndex: [0],
          start: getDataZoomRange(years)[0],
          end: getDataZoomRange(years)[1],
          bottom: 15,
          height: 20,
          backgroundColor: '#f8f9fa',
          borderColor: '#e9ecef',
          fillerColor: 'rgba(135, 206, 250, 0.3)',
          selectedDataBackground: {
            areaStyle: {
              color: 'rgba(135, 206, 250, 0.2)'
            },
            lineStyle: {
              color: '#87ceeb',
              width: 1
            }
          },
          handleStyle: {
            color: '#87ceeb',
            borderColor: '#87ceeb',
            borderWidth: 1,
            shadowBlur: 2,
            shadowColor: 'rgba(0, 0, 0, 0.1)'
          },
          textStyle: {
            color: '#666',
            fontSize: 14,
            fontFamily: "'Poppins', sans-serif"
          },
          showDetail: false,
          showDataShadow: false,
          realtime: true,
          filterMode: 'filter'
        },
        {
          type: 'inside',
          xAxisIndex: [0],
          start: getDataZoomRange(years)[0],
          end: getDataZoomRange(years)[1],
          zoomLock: true,
          zoomOnMouseWheel: false,
          moveOnMouseMove: false,
          moveOnMouseWheel: false
        }
      ],
      xAxis: {
        type: 'category',
        data: years,
        axisLine: {
          lineStyle: {
            color: '#e5e5e5'
          }
        },
        axisTick: {
          show: false
        },
        axisLabel: {
          color: '#666',
          fontSize: 14,
          fontFamily: '"Poppins", sans-serif'
        }
      },
      yAxis: {
        type: 'value',
        name: 'RISE Score',
        nameLocation: 'middle',
        nameGap: 50,
        axisLine: {
          lineStyle: {
            color: '#e5e5e5'
          }
        },
        axisTick: {
          show: false
        },
        axisLabel: {
          color: '#666',
          fontSize: 14,
          fontFamily: '"Poppins", sans-serif',
          show: true
        },
        nameTextStyle: {
          color: '#666',
          fontSize: 14,
          fontFamily: '"Poppins", sans-serif',
          padding: [0, 0, 0, 0]
        },
        splitLine: {
          lineStyle: {
            color: '#f0f0f0',
            type: 'dashed'
          }
        }
      },
      series: [
        {
          name: 'Evolution of total score of RISE indicator',
          type: 'line',
          data: scores,
          smooth: false,
          symbol: 'circle',
          symbolSize: 8,
          itemStyle: {
            color: '#1ABC9C'
          },
          lineStyle: {
            color: '#1ABC9C',
            width: 2
          },
          areaStyle: {
            color: 'transparent'
          }
        }
      ],
      media: [
        {
          query: { maxWidth: 1024 }, // Tablet and below
          option: {
            grid: {
              left: '8%', // Increased to ensure yAxis label is always visible
              right: '8%', // Increased to ensure yAxis label is always visible
              top: '12%',
              bottom: '25%'
            },
            legend: {
              bottom: 40,
              itemWidth: 10,
              itemHeight: 10,
              textStyle: { fontSize: 12 }
            },
            xAxis: {
              axisLabel: { fontSize: 12 }
            },
            yAxis: {
              axisLabel: { fontSize: 12, show: true },
              nameTextStyle: { fontSize: 12, padding: [0, 0, 0, 0] }
            }
          }
        },
        {
          query: { maxWidth: 768 }, // Mobile
          option: {
            grid: {
              left: '12%', // Increased to ensure yAxis label is always visible on mobile
              right: '8%', // Increased to ensure yAxis label is always visible on mobile
              bottom: '25%'
            },
            legend: {
              show: true,
              bottom: 40,
              textStyle: { fontSize: 11 },
            },
            xAxis: {
              axisLabel: { fontSize: 11 }
            },
            yAxis: {
              name: 'Population (Million)',
              nameGap: 40, // Reduced gap for mobile
              axisLabel: { fontSize: 11, show: true },
              nameTextStyle: { fontSize: 11, padding: [0, 0, 0, 0] }
            }
          }
        }
      ]
    };
  }, [cleanCookingData]);

  // ECharts options for modal view (larger fonts)
  const cleanCookingChartOptionModal = useMemo(() => {
    if (!cleanCookingChartOption || Object.keys(cleanCookingChartOption).length === 0) {
      return {};
    }
    return {
      ...cleanCookingChartOption,
      tooltip: {
        ...cleanCookingChartOption.tooltip,
        textStyle: {
          fontFamily: '"Poppins", sans-serif',
          fontSize: 14
        }
      },
      legend: {
        ...cleanCookingChartOption.legend,
        bottom: 50,
        textStyle: {
          ...cleanCookingChartOption.legend.textStyle,
          fontSize: 14
        }
      },
      grid: {
        left: '8%', // Increased to ensure yAxis label is always visible
        right: '8%', // Increased to ensure yAxis label is always visible
        bottom: '30%',
        top: '10%',
        containLabel: true
      },
      xAxis: {
        ...cleanCookingChartOption.xAxis,
        axisLabel: {
          ...cleanCookingChartOption.xAxis.axisLabel,
          fontSize: 14
        }
      },
      yAxis: {
        ...cleanCookingChartOption.yAxis,
        axisLabel: {
          ...cleanCookingChartOption.yAxis.axisLabel,
          fontSize: 14
        },
        nameTextStyle: {
          ...cleanCookingChartOption.yAxis.nameTextStyle,
          fontSize: 14
        }
      }
    };
  }, [cleanCookingChartOption]);

  // ECharts options for Energy Efficiency RISE indicator line chart
  const energyEfficiencyChartOption = useMemo(() => {
    if (!energyEfficiencyData || energyEfficiencyData.length === 0) {
      return {};
    }

    const years = energyEfficiencyData.map(item => item.year.toString());
    const scores = energyEfficiencyData.map(item => {
      const score = item.score;
      // Filter out non-numeric values (like "-")
      if (score === null || score === undefined || score === '-' || isNaN(parseFloat(score))) {
        return null;
      }
      return parseFloat(score);
    });

    return {
      tooltip: {
        trigger: 'axis',
        axisPointer: {
          type: 'line'
        },
        formatter: function (params) {
          if (!params || !Array.isArray(params) || params.length === 0) {
            return '';
          }
          const param = params[0];
          const val = param.value;
          const value = (val !== null && val !== undefined && !isNaN(val)) ? val.toFixed(2) : 'N/A';
          const year = param.axisValue || param.name || '';
          return '<div style="padding: 5px; background: white; border-radius: 8px; font-family: \'Poppins\', sans-serif;">' +
            '<div style="font-size: 14px; font-weight: 500; color: #333; margin-bottom: 8px; font-family: \'Poppins\', sans-serif;">' +
            year + '</div>' +
            '<div style="color: #666; font-size:14px; font-weight:500; font-family: \'Poppins\', sans-serif;">' +
            '<span style="color:' + (param.color || '#9B59B6') + '">●</span> ' +
            'Score: <span style="font-weight: 600; color: #333;">' + value + '</span>' +
            '</div>' +
            '</div>';
        },
        backgroundColor: 'rgba(255, 255, 255, 0.98)',
        borderColor: '#e5e5e5',
        borderWidth: 1,
        borderRadius: 8,
        textStyle: {
          fontFamily: "'Poppins', sans-serif",
          fontSize: 14,
          color: '#666666',
          fontWeight: '500'
        }
      },
      legend: {
        data: ['Evolution of total score of RISE indicator'],
        bottom: 50,
        left: 'center',
        itemWidth: 12,
        itemHeight: 12,
        itemGap: 8,
        textStyle: {
          fontFamily: '"Poppins", sans-serif',
          fontSize: 14,
          color: '#181818',
          fontWeight: 'normal'
        },
        icon: 'circle'
      },
      grid: {
        left: '3%',
        right: '4%',
        bottom: '30%',
        top: '10%',
        containLabel: true
      },
      dataZoom: [
        {
          type: 'slider',
          show: true,
          xAxisIndex: [0],
          start: getDataZoomRange(years)[0],
          end: getDataZoomRange(years)[1],
          bottom: 15,
          height: 20,
          backgroundColor: '#f8f9fa',
          borderColor: '#e9ecef',
          fillerColor: 'rgba(135, 206, 250, 0.3)',
          selectedDataBackground: {
            areaStyle: {
              color: 'rgba(135, 206, 250, 0.2)'
            },
            lineStyle: {
              color: '#87ceeb',
              width: 1
            }
          },
          handleStyle: {
            color: '#87ceeb',
            borderColor: '#87ceeb',
            borderWidth: 1,
            shadowBlur: 2,
            shadowColor: 'rgba(0, 0, 0, 0.1)'
          },
          textStyle: {
            color: '#666',
            fontSize: 14,
            fontFamily: "'Poppins', sans-serif"
          },
          showDetail: false,
          showDataShadow: false,
          realtime: true,
          filterMode: 'filter'
        },
        {
          type: 'inside',
          xAxisIndex: [0],
          start: getDataZoomRange(years)[0],
          end: getDataZoomRange(years)[1],
          zoomLock: true,
          zoomOnMouseWheel: false,
          moveOnMouseMove: false,
          moveOnMouseWheel: false
        }
      ],
      xAxis: {
        type: 'category',
        data: years,
        axisLine: {
          lineStyle: {
            color: '#e5e5e5'
          }
        },
        axisTick: {
          show: false
        },
        axisLabel: {
          color: '#666',
          fontSize: 14,
          fontFamily: '"Poppins", sans-serif'
        }
      },
      yAxis: {
        type: 'value',
        name: 'RISE Score',
        nameLocation: 'middle',
        nameGap: 50,
        // min: 0,
        // // Calculate max dynamically based on data, with padding
        // max: (function() {
        //   const maxValue = Math.max(...scores.filter(s => s !== null && s !== undefined));
        //   if (maxValue <= 0) return 75;
        //   // Round up to nearest 25 and add 25 for padding
        //   return Math.ceil((maxValue + 25) / 25) * 25;
        // })(),
        // interval: (function() {
        //   const maxValue = Math.max(...scores.filter(s => s !== null && s !== undefined));
        //   if (maxValue <= 0) return 25;
        //   const calculatedMax = Math.ceil((maxValue + 25) / 25) * 25;
        //   // Determine interval based on max value
        //   if (calculatedMax <= 100) return 25;
        //   if (calculatedMax <= 200) return 50;
        //   return 100;
        // })(),
        axisLine: {
          lineStyle: {
            color: '#e5e5e5'
          }
        },
        axisTick: {
          show: false
        },
        axisLabel: {
          color: '#666',
          fontSize: 14,
          fontFamily: '"Poppins", sans-serif',
          show: true
        },
        nameTextStyle: {
          color: '#666',
          fontSize: 14,
          fontFamily: '"Poppins", sans-serif',
          padding: [0, 0, 0, 0]
        },
        splitLine: {
          lineStyle: {
            color: '#f0f0f0',
            type: 'dashed'
          }
        }
      },
      series: [
        {
          name: 'Evolution of total score of RISE indicator',
          type: 'line',
          data: scores,
          smooth: false,
          symbol: 'circle',
          symbolSize: 8,
          itemStyle: {
            color: '#9B59B6'
          },
          lineStyle: {
            color: '#9B59B6',
            width: 2
          },
          areaStyle: {
            color: 'transparent'
          }
        }
      ],
      media: [
        // Tablet
        {
          query: { maxWidth: 1024 },
          option: {
            legend: {
              bottom: 40,
              itemWidth: 10,
              itemHeight: 10,
              itemGap: 6,
              textStyle: { fontSize: 11 }
            },
            grid: {
              bottom: '25%'
            }
          }
        },
        // Mobile
        {
          query: { maxWidth: 768 },
          option: {
            legend: {
              bottom: 40,
              itemWidth: 8,
              itemHeight: 8,
              itemGap: 4,
              textStyle: { fontSize: 11 }
            },
            grid: {
              left: '12%', // Increased to ensure yAxis label is always visible on mobile
              right: '8%', // Increased to ensure yAxis label is always visible
              bottom: '25%'
            },
            xAxis: {
              axisLabel: { fontSize: 11 }
            },
            yAxis: {
              name: 'RISE Score',
              nameGap: 35, // Reduced gap for mobile
              axisLabel: { fontSize: 11, show: true },
              nameTextStyle: { fontSize: 11, padding: [0, 0, 0, 0] }
            }
          }
        }
      ]
    };
  }, [energyEfficiencyData]);

  // ECharts options for modal view (larger fonts)
  const energyEfficiencyChartOptionModal = useMemo(() => {
    if (!energyEfficiencyChartOption || Object.keys(energyEfficiencyChartOption).length === 0) {
      return {};
    }
    return {
      ...energyEfficiencyChartOption,
      tooltip: {
        ...energyEfficiencyChartOption.tooltip,
        textStyle: {
          fontFamily: '"Poppins", sans-serif',
          fontSize: 14
        }
      },
      legend: {
        ...energyEfficiencyChartOption.legend,
        bottom: 50,
        textStyle: {
          ...energyEfficiencyChartOption.legend.textStyle,
          fontSize: 14
        }
      },
      grid: {
        left: '8%', // Increased to ensure yAxis label is always visible
        right: '8%', // Increased to ensure yAxis label is always visible
        bottom: '30%',
        top: '10%',
        containLabel: true
      },
      xAxis: {
        ...energyEfficiencyChartOption.xAxis,
        axisLabel: {
          ...energyEfficiencyChartOption.xAxis.axisLabel,
          fontSize: 14
        }
      },
      yAxis: {
        ...energyEfficiencyChartOption.yAxis,
        axisLabel: {
          ...energyEfficiencyChartOption.yAxis.axisLabel,
          fontSize: 14
        },
        nameTextStyle: {
          ...energyEfficiencyChartOption.yAxis.nameTextStyle,
          fontSize: 14
        }
      }
    };
  }, [energyEfficiencyChartOption]);

  // ECharts options for Electricity Demand (Final Consumption) line chart
  const electricityDemandChartOption = useMemo(() => {
    if (!electricityDemandData || electricityDemandData.length === 0) {
      return {};
    }

    const years = electricityDemandData.map(item => item.year.toString());
    const finalConsumption = electricityDemandData.map(item => item.finalConsumption);
    const finalConsumptionPerCapita = electricityDemandData.map(item => item.finalConsumptionPerCapita);

    // Calculate max values for Y-axis scaling
    const maxConsumption = Math.max(...finalConsumption.filter(v => v !== null && v !== undefined));
    const maxPerCapita = Math.max(...finalConsumptionPerCapita.filter(v => v !== null && v !== undefined));

    return {
      tooltip: {
        trigger: 'axis',
        axisPointer: {
          type: 'line'
        },
        formatter: function (params) {
          if (!params || !Array.isArray(params) || params.length === 0) {
            return '';
          }
          const year = params[0].axisValue || params[0].name || '';
          let items = '';
          params.forEach(function (item, index) {
            const val = item.value;
            const value = (val !== null && val !== undefined && !isNaN(val)) ? val.toFixed(2) : 'N/A';
            const unit = item.seriesName.includes('per capita') ? ' KWh' : ' GWh';
            const isLast = index === params.length - 1;
            const borderStyle = isLast ? 'border-top: 1px solid #eee; padding-top: 4px; margin-top: 4px;' : '';
            items += '<div style="color: #666; font-size:14px; font-weight:500; margin-bottom: 4px; font-family: \'Poppins\', sans-serif; ' + borderStyle + '">' +
              '<span style="color:' + (item.color || '#3498DB') + '">●</span> ' +
              item.seriesName + ': <span style="font-weight: 600; color: #333;">' + value + unit + '</span>' +
              '</div>';
          });
          return '<div style="padding: 5px; background: white; border-radius: 8px; font-family: \'Poppins\', sans-serif;">' +
            '<div style="font-size: 14px; font-weight: 500; color: #333; margin-bottom: 8px; font-family: \'Poppins\', sans-serif;">' +
            year + '</div>' + items + '</div>';
        },
        backgroundColor: 'rgba(255, 255, 255, 0.98)',
        borderColor: '#e5e5e5',
        borderWidth: 1,
        borderRadius: 8,
        textStyle: {
          fontFamily: "'Poppins', sans-serif",
          fontSize: 14,
          color: '#666666',
          fontWeight: '500'
        }
      },
      legend: {
        data: ['Electricity final consumption (GWh)', 'Electricity final consumption per capita (KWh)'],
        bottom: 50,
        left: 'center',
        itemWidth: 12,
        itemHeight: 12,
        itemGap: 24,
        textStyle: {
          fontFamily: "'Poppins', sans-serif",
          fontSize: 14,
          color: '#181818',
          fontWeight: 'normal'
        },
        icon: 'circle'
      },
      grid: {
        left: '8%',
        right: '8%',
        bottom: '30%',
        top: '10%',
        containLabel: true
      },
      dataZoom: [
        {
          type: 'slider',
          show: true,
          xAxisIndex: [0],
          start: getDataZoomRange(years)[0],
          end: getDataZoomRange(years)[1],
          bottom: 15,
          height: 20,
          backgroundColor: '#f8f9fa',
          borderColor: '#e9ecef',
          fillerColor: 'rgba(135, 206, 250, 0.3)',
          selectedDataBackground: {
            areaStyle: {
              color: 'rgba(135, 206, 250, 0.2)'
            },
            lineStyle: {
              color: '#87ceeb',
              width: 1
            }
          },
          handleStyle: {
            color: '#87ceeb',
            borderColor: '#87ceeb',
            borderWidth: 1,
            shadowBlur: 2,
            shadowColor: 'rgba(0, 0, 0, 0.1)'
          },
          textStyle: {
            color: '#666',
            fontSize: 14,
            fontFamily: "'Poppins', sans-serif"
          },
          showDetail: false,
          showDataShadow: false,
          realtime: true,
          filterMode: 'filter'
        },
        {
          type: 'inside',
          xAxisIndex: [0],
          start: getDataZoomRange(years)[0],
          end: getDataZoomRange(years)[1],
          zoomLock: true,
          zoomOnMouseWheel: false,
          moveOnMouseMove: false,
          moveOnMouseWheel: false
        }
      ],
      xAxis: {
        type: 'category',
        data: years,
        axisLine: {
          lineStyle: {
            color: '#e5e5e5'
          }
        },
        axisTick: {
          show: false
        },
        axisLabel: {
          color: '#666',
          fontSize: 14,
          fontFamily: '"Poppins", sans-serif'
        }
      },
      yAxis: [
        {
          type: 'value',
          name: 'GWh',
          nameLocation: 'middle',
          nameGap: 40,
          position: 'left',
          min: 0,
          max: maxConsumption > 0 ? Math.ceil((maxConsumption * 1.1) / 10000) * 10000 : 200000,
          interval: maxConsumption > 0 ? Math.ceil((maxConsumption * 1.1) / 10000) * 10000 / 5 : 40000,
          axisLine: {
            lineStyle: {
              color: '#e5e5e5'
            }
          },
          axisTick: {
            show: false
          },
        axisLabel: {
          color: '#666',
          fontSize: 14,
          fontFamily: '"Poppins", sans-serif',
          show: true,
          formatter: function (value) {
              return (value / 1000).toFixed(0) + 'k';
            }
          },
          nameTextStyle: {
            color: '#666',
            fontSize: 12,
            fontFamily: '"Poppins", sans-serif',
            padding: [0, 0, 0, 0]
          },
          splitLine: {
            lineStyle: {
              color: '#f0f0f0',
              type: 'dashed'
            }
          }
        },
        {
          type: 'value',
          name: 'KWh',
          nameLocation: 'middle',
          nameGap: 40,
          position: 'right',
          min: 0,
          max: maxPerCapita > 0 ? Math.ceil((maxPerCapita * 1.1) / 100) * 100 : 2000,
          interval: maxPerCapita > 0 ? Math.ceil((maxPerCapita * 1.1) / 100) * 100 / 5 : 400,
          axisLine: {
            lineStyle: {
              color: '#e5e5e5'
            }
          },
          axisTick: {
            show: false
          },
          axisLabel: {
            color: '#666',
            fontSize: 12,
            fontFamily: '"Poppins", sans-serif',
            show: true
          },
          nameTextStyle: {
            color: '#666',
            fontSize: 12,
            fontFamily: '"Poppins", sans-serif',
            padding: [0, 0, 0, 0]
          },
          splitLine: {
            show: false
          }
        }
      ],
      series: [
        {
          name: 'Electricity final consumption (GWh)',
          type: 'line',
          yAxisIndex: 0,
          data: finalConsumption,
          smooth: false,
          symbol: 'circle',
          symbolSize: 8,
          itemStyle: {
            color: '#DAA520'
          },
          lineStyle: {
            color: '#DAA520',
            width: 2
          },
          areaStyle: {
            color: 'transparent'
          }
        },
        {
          name: 'Electricity final consumption per capita (KWh)',
          type: 'line',
          yAxisIndex: 1,
          data: finalConsumptionPerCapita,
          smooth: false,
          symbol: 'diamond',
          symbolSize: 8,
          itemStyle: {
            color: '#E35335'
          },
          lineStyle: {
            color: '#E35335',
            width: 2
          },
          areaStyle: {
            color: 'transparent'
          }
        }
      ],
      media: [
        {
          query: { maxWidth: 1024 }, // tablets and below
          option: {
            legend: {
              itemWidth: 10,
              itemHeight: 10,
              itemGap: 14,
              textStyle: { fontSize: 12 }
            },
            grid: { bottom: '25%' }
          }
        },
        {
          query: { maxWidth: 768 }, // mobile
          option: {
            legend: {
              itemWidth: 8,
              itemHeight: 8,
              itemGap: 8,
              textStyle: { fontSize: 11 }
            },
            grid: { 
              left: '14%', // Increased to ensure yAxis label is always visible on mobile
              right: '14%', // Increased for right yAxis label on mobile
              bottom: '30%' 
            },
            xAxis: {
              axisLabel: { fontSize: 11 }
            },
            yAxis: [
              { 
                name: 'GWh',
                nameGap: 40, // Reduced gap for mobile
                axisLabel: { fontSize: 11, show: true }, 
                nameTextStyle: { fontSize: 11, padding: [0, 0, 0, 0] } 
              },
              { 
                name: 'KWh',
                nameGap: 40, // Reduced gap for mobile
                axisLabel: { fontSize: 11, show: true }, 
                nameTextStyle: { fontSize: 11, padding: [0, 0, 0, 0] } 
              }
            ]
          }
        }
      ]
    };
  }, [electricityDemandData]);

  // ECharts options for modal view (larger fonts)
  const electricityDemandChartOptionModal = useMemo(() => {
    if (!electricityDemandChartOption || Object.keys(electricityDemandChartOption).length === 0) {
      return {};
    }
    return {
      ...electricityDemandChartOption,
      tooltip: {
        ...electricityDemandChartOption.tooltip,
        textStyle: {
          fontFamily: '"Poppins", sans-serif',
          fontSize: 14
        }
      },
      legend: {
        ...electricityDemandChartOption.legend,
        bottom: 50,
        textStyle: {
          ...electricityDemandChartOption.legend.textStyle,
          fontSize: 14
        }
      },
      grid: {
        left: '8%',
        right: '8%',
        bottom: '30%',
        top: '10%',
        containLabel: true
      },
      xAxis: {
        ...electricityDemandChartOption.xAxis,
        axisLabel: {
          ...electricityDemandChartOption.xAxis.axisLabel,
          fontSize: 14
        }
      },
      yAxis: [
        {
          ...electricityDemandChartOption.yAxis[0],
          axisLabel: {
            ...electricityDemandChartOption.yAxis[0].axisLabel,
            fontSize: 14
          },
          nameTextStyle: {
            ...electricityDemandChartOption.yAxis[0].nameTextStyle,
            fontSize: 14
          }
        },
        {
          ...electricityDemandChartOption.yAxis[1],
          axisLabel: {
            ...electricityDemandChartOption.yAxis[1].axisLabel,
            fontSize: 14
          },
          nameTextStyle: {
            ...electricityDemandChartOption.yAxis[1].nameTextStyle,
            fontSize: 14
          }
        }
      ]
    };
  }, [electricityDemandChartOption]);

  // ECharts options for Energy Intensity line chart
  const energyIntensityChartOption = useMemo(() => {
    // console.log('Energy Intensity Chart Option - energyIntensityData:', energyIntensityData);
    if (!energyIntensityData || energyIntensityData.length === 0) {
      // console.log('Energy Intensity Chart Option - No data, returning empty object');
      return {};
    }

    const years = energyIntensityData.map(item => item.year.toString());
    const intensities = energyIntensityData.map(item => item.intensity || null);

    // Calculate max value for Y-axis scaling
    const maxIntensity = Math.max(...intensities.filter(v => v !== null && v !== undefined));

    const chartOption = {
      tooltip: {
        trigger: 'axis',
        axisPointer: {
          type: 'line'
        },
        formatter: function (params) {
          if (!params || !Array.isArray(params) || params.length === 0) {
            return '';
          }
          const param = params[0];
          const val = param.value;
          const value = (val !== null && val !== undefined && !isNaN(val)) ? val.toFixed(2) : 'N/A';
          const year = param.axisValue || param.name || '';
          return '<div style="padding: 5px; background: white; border-radius: 8px; font-family: \'Poppins\', sans-serif;">' +
            '<div style="font-size: 14px; font-weight: 500; color: #333; margin-bottom: 8px; font-family: \'Poppins\', sans-serif;">' +
            year + '</div>' +
            '<div style="color: #666; font-size:14px; font-weight:500; font-family: \'Poppins\', sans-serif;">' +
            '<span style="color:' + (param.color || '#FFC50F') + '">●</span> ' +
            'Energy Intensity: <span style="font-weight: 600; color: #333;">' + value + ' MJ/USD PPP 2017</span>' +
            '</div>' +
            '</div>';
        },
        backgroundColor: 'rgba(255, 255, 255, 0.98)',
        borderColor: '#e5e5e5',
        borderWidth: 1,
        borderRadius: 8,
        textStyle: {
          fontFamily: "'Poppins', sans-serif",
          fontSize: 14,
          color: '#666666',
          fontWeight: '500'
        }
      },
      legend: {
        data: ['Energy intensity level of primary energy (MJ/2017 PPP GDP)'],
        bottom: 50,
        left: 'center',
        itemWidth: 12,
        itemHeight: 12,
        itemGap: 8,
        textStyle: {
          fontFamily: '"Poppins", sans-serif',
          fontSize: 14,
          color: '#181818',
          fontWeight: 'normal'
        },
        icon: 'circle'
      },
      grid: {
        left: '12%', // Increased to ensure yAxis label "MJ/USD PPP 2017" is always visible (longer label)
        right: '8%', // Increased to ensure yAxis label is always visible
        bottom: '30%',
        top: '10%',
        containLabel: true
      },
      dataZoom: [
        {
          type: 'slider',
          show: true,
          xAxisIndex: [0],
          start: getDataZoomRange(years)[0],
          end: getDataZoomRange(years)[1],
          bottom: 15,
          height: 20,
          backgroundColor: '#f8f9fa',
          borderColor: '#e9ecef',
          fillerColor: 'rgba(135, 206, 250, 0.3)',
          selectedDataBackground: {
            areaStyle: {
              color: 'rgba(135, 206, 250, 0.2)'
            },
            lineStyle: {
              color: '#87ceeb',
              width: 1
            }
          },
          handleStyle: {
            color: '#87ceeb',
            borderColor: '#87ceeb',
            borderWidth: 1,
            shadowBlur: 2,
            shadowColor: 'rgba(0, 0, 0, 0.1)'
          },
          textStyle: {
            color: '#666',
            fontSize: 14,
            fontFamily: '"Poppins", sans-serif'
          },
          showDetail: false,
          showDataShadow: false,
          realtime: true,
          filterMode: 'filter'
        },
        {
          type: 'inside',
          xAxisIndex: [0],
          start: getDataZoomRange(years)[0],
          end: getDataZoomRange(years)[1],
          zoomLock: true,
          zoomOnMouseWheel: false,
          moveOnMouseMove: false,
          moveOnMouseWheel: false
        }
      ],
      xAxis: {
        type: 'category',
        data: years,
        axisLine: {
          lineStyle: {
            color: '#e5e5e5'
          }
        },
        axisTick: {
          show: false
        },
        axisLabel: {
          color: '#666',
          fontSize: 12,
          fontFamily: '"Poppins", sans-serif'
        }
      },
      yAxis: {
        type: 'value',
        name: 'MJ/USD PPP 2017',
        nameLocation: 'middle',
        nameGap: 30, // Increased for longer label text
        // min: 0,
        // max: maxIntensity > 0 ? Math.ceil((maxIntensity * 1.1) / 1) * 1 : 6,
        // interval: maxIntensity > 0 && maxIntensity <= 6 ? 2 : (maxIntensity <= 12 ? 4 : 6),
        axisLine: {
          lineStyle: {
            color: '#e5e5e5'
          }
        },
        axisTick: {
          show: false
        },
        axisLabel: {
          color: '#666',
          fontSize: 12,
          fontFamily: '"Poppins", sans-serif',
          show: true
        },
        nameTextStyle: {
          color: '#666',
          fontSize: 14,
          fontFamily: '"Poppins", sans-serif',
          padding: [0, 0, 0, 0]
        },
        splitLine: {
          lineStyle: {
            color: '#f0f0f0',
            type: 'dashed'
          }
        }
      },
      series: [
        {
          name: 'Energy intensity level of primary energy (MJ/2017 PPP GDP)',
          type: 'line',
          data: intensities,
          smooth: false,
          symbol: 'circle',
          symbolSize: 8,
          itemStyle: {
            color: '#FFC50F'
          },
          lineStyle: {
            color: '#FFC50F',
            width: 2
          },
          areaStyle: {
            color: 'transparent'
          }
        }
      ],
      media: [
        {
          query: { maxWidth: 1024 }, // tablet and below
          option: {
            legend: {
              bottom: 40,
              itemWidth: 10,
              itemHeight: 10,
              itemGap: 6,
              textStyle: { fontSize: 12 }
            },
            grid: {
              bottom: '25%'
            }
          }
        },
        {
          query: { maxWidth: 768 }, // mobile
          option: {
            legend: {
              bottom: 40,
              itemWidth: 8,
              itemHeight: 8,
              itemGap: 4,
              textStyle: { fontSize: 11 },
              formatter: function(name) {
                // Use shorter text on mobile to prevent cutting
                if (name === 'Energy intensity level of primary energy (MJ/2017 PPP GDP)') {
                  return 'Energy Intensity (MJ/2017 PPP GDP)';
                }
                return name;
              },
              width: '90%', // Limit legend width
              left: 'center'
            },
            grid: {
              left: '14%', // Increased to ensure yAxis label "MJ/USD PPP 2017" is always visible on mobile
              right: '8%', // Increased to ensure yAxis label is always visible
              bottom: '25%' // Increased bottom to accommodate wrapped legend
            },
            xAxis: {
              axisLabel: { fontSize: 11 }
            },
            yAxis: {
              name: 'MJ/USD PPP 2017',
              nameGap: 35, // Reduced gap for mobile
              axisLabel: { fontSize: 11, show: true },
              nameTextStyle: { fontSize: 11, padding: [0, 0, 0, 0] }
            }
          }
        }
      ]
    };

    // Console log the chart option to debug

    return chartOption;
  }, [energyIntensityData]);

  // ECharts options for modal view (larger fonts)
  const energyIntensityChartOptionModal = useMemo(() => {
    if (!energyIntensityChartOption || Object.keys(energyIntensityChartOption).length === 0) {
      return {};
    }
    return {
      ...energyIntensityChartOption,
      tooltip: {
        ...energyIntensityChartOption.tooltip,
        textStyle: {
          fontFamily: "'Poppins', sans-serif",
          fontSize: 14
        }
      },
      legend: {
        ...energyIntensityChartOption.legend,
        bottom: 50,
        textStyle: {
          ...energyIntensityChartOption.legend.textStyle,
          fontSize: 14
        }
      },
      grid: {
        left: '12%', // Increased to ensure yAxis label "MJ/USD PPP 2017" is always visible (longer label in modal)
        right: '8%', // Increased to ensure yAxis label is always visible
        bottom: '30%',
        top: '10%',
        containLabel: true
      },
      xAxis: {
        ...energyIntensityChartOption.xAxis,
        axisLabel: {
          ...energyIntensityChartOption.xAxis.axisLabel,
          fontSize: 14
        }
      },
      yAxis: {
        ...energyIntensityChartOption.yAxis,
        nameGap: 60, // Increased for longer label text in modal
        axisLabel: {
          ...energyIntensityChartOption.yAxis.axisLabel,
          fontSize: 14
        },
        nameTextStyle: {
          ...energyIntensityChartOption.yAxis.nameTextStyle,
          fontSize: 14
        }
      }
    };
  }, [energyIntensityChartOption]);

  // ECharts options for Electricity Installed Capacity bar chart
  const electricityCapacityChartOption = useMemo(() => {
    if (!electricityCapacityData || electricityCapacityData.length === 0) {
      return {};
    }

    const years = electricityCapacityData.map(item => item.year.toString());
    const capacities = electricityCapacityData.map(item => item.capacity || null);

    // Calculate max value for Y-axis scaling
    const maxCapacity = Math.max(...capacities.filter(v => v !== null && v !== undefined));
    const yAxisMax = maxCapacity > 0 ? Math.ceil((maxCapacity * 1.1) / 10000) * 10000 : 30000;
    const yAxisInterval = yAxisMax / 3;

    return {
      tooltip: {
        trigger: 'axis',
        axisPointer: {
          type: 'shadow'
        },
        formatter: function (params) {
          if (!params || !Array.isArray(params) || params.length === 0) {
            return '';
          }
          const param = params[0];
          const val = param.value;
          const value = (val !== null && val !== undefined && !isNaN(val)) ? val.toLocaleString('en-US', { maximumFractionDigits: 0 }) : 'N/A';
          const year = param.axisValue || param.name || '';
          return '<div style="padding: 5px; background: white; border-radius: 8px; font-family: \'Poppins\', sans-serif;">' +
            '<div style="font-size: 14px; font-weight: 500; color: #333; margin-bottom: 8px; font-family: \'Poppins\', sans-serif;">' +
            year + '</div>' +
            '<div style="color: #666; font-size:14px; font-weight:500; font-family: \'Poppins\', sans-serif;">' +
            '<span style="color:' + (param.color || '#32CD32') + '">●</span> ' +
            'Electricity installed capacity: <span style="font-weight: 600; color: #333;">' + value + ' MW</span>' +
            '</div>' +
            '</div>';
        },
        backgroundColor: 'rgba(255, 255, 255, 0.98)',
        borderColor: '#e5e5e5',
        borderWidth: 1,
        borderRadius: 8,
        textStyle: {
          fontFamily: "'Poppins', sans-serif",
          fontSize: 14,
          color: '#666666',
          fontWeight: '500'
        }
      },
      legend: {
        data: ['Electricity installed capacity, Total (MW)'],
        bottom: 50,
        left: 'center',
        itemWidth: 12,
        itemHeight: 12,
        itemGap: 8,
        textStyle: {
          fontFamily: "'Poppins', sans-serif",
          fontSize: 14,
          color: '#181818',
          fontWeight: 'normal'
        },
        icon: 'circle'
      },
      grid: {
        left: '3%',
        right: '4%',
        bottom: '30%',
        top: '10%',
        containLabel: true
      },
      dataZoom: [
        {
          type: 'slider',
          show: true,
          xAxisIndex: [0],
          start: getDataZoomRange(years)[0],
          end: getDataZoomRange(years)[1],
          bottom: 15,
          height: 20,
          backgroundColor: '#f8f9fa',
          borderColor: '#e9ecef',
          fillerColor: 'rgba(135, 206, 250, 0.3)',
          selectedDataBackground: {
            areaStyle: {
              color: 'rgba(135, 206, 250, 0.2)'
            },
            lineStyle: {
              color: '#87ceeb',
              width: 1
            }
          },
          handleStyle: {
            color: '#87ceeb',
            borderColor: '#87ceeb',
            borderWidth: 1,
            shadowBlur: 2,
            shadowColor: 'rgba(0, 0, 0, 0.1)'
          },
          textStyle: {
            color: '#666',
            fontSize: 14,
            fontFamily: "'Poppins', sans-serif"
          },
          showDetail: false,
          showDataShadow: false,
          realtime: true,
          filterMode: 'filter'
        },
        {
          type: 'inside',
          xAxisIndex: [0],
          start: getDataZoomRange(years)[0],
          end: getDataZoomRange(years)[1],
          zoomLock: true,
          zoomOnMouseWheel: false,
          moveOnMouseMove: false,
          moveOnMouseWheel: false
        }
      ],
      xAxis: {
        type: 'category',
        data: years,
        axisLine: {
          lineStyle: {
            color: '#e5e5e5'
          }
        },
        axisTick: {
          show: false
        },
        axisLabel: {
          color: '#666',
          fontSize: 14,
          fontFamily: '"Poppins", sans-serif'
        }
      },
      yAxis: {
        type: 'value',
        name: 'MW',
        nameLocation: 'middle',
        nameGap: 50,
        // min: 0,
        // max: yAxisMax,
        // interval: yAxisInterval,
        axisLine: {
          lineStyle: {
            color: '#e5e5e5'
          }
        },
        axisTick: {
          show: false
        },
        axisLabel: {
          color: '#666',
          fontSize: 14,
          fontFamily: '"Poppins", sans-serif',
          show: true,
          formatter: function (value) {
            if (value >= 1000) {
              const kValue = Math.round(value / 1000);
              return kValue + 'k';
            }
            return Math.round(value).toString();
          }
        },
        nameTextStyle: {
          color: '#666',
          fontSize: 14,
          fontFamily: '"Poppins", sans-serif',
          padding: [0, 0, 0, 0]
        },
        splitLine: {
          lineStyle: {
            color: '#f0f0f0',
            type: 'dashed'
          }
        }
      },
      series: [
        {
          name: 'Electricity installed capacity, Total (MW)',
          type: 'bar',
          data: capacities,
          itemStyle: {
            // color: '#8CB9BD' // Lime green - vibrant and appealing
            color: '#95956E'
          },
          barWidth: '60%'
        }
      ],
      media: [
        {
          query: { maxWidth: 1024 }, // Tablet and below
          option: {
            grid: {
              left: '8%', // Increased to ensure yAxis label is always visible
              right: '8%', // Increased to ensure yAxis label is always visible
              top: '12%',
              bottom: '25%'
            },
            legend: {
              bottom: 40,
              itemWidth: 10,
              itemHeight: 10,
              textStyle: { fontSize: 12 }
            },
            xAxis: {
              axisLabel: { fontSize: 12 }
            },
            yAxis: {
              axisLabel: { fontSize: 12, show: true },
              nameTextStyle: { fontSize: 12, padding: [0, 0, 0, 0] }
            }
          }
        },
        {
          query: { maxWidth: 768 }, // Mobile
          option: {
            grid: {
              left: '12%', // Increased to ensure yAxis label is always visible on mobile
              right: '8%', // Increased to ensure yAxis label is always visible on mobile
              bottom: '25%'
            },
            legend: {
              show: true,
              bottom: 40
            },
            xAxis: {
              axisLabel: { fontSize: 11 }
            },
            yAxis: {
              name: 'MW',
              nameGap: 40, // Reduced gap for mobile
              axisLabel: { fontSize: 11, show: true },
              nameTextStyle: { fontSize: 11, padding: [0, 0, 0, 0] }
            }
          }
        }
      ]
    };
  }, [electricityCapacityData]);

  // ECharts options for modal view (larger fonts)
  const electricityCapacityChartOptionModal = useMemo(() => {
    if (!electricityCapacityChartOption || Object.keys(electricityCapacityChartOption).length === 0) {
      return {};
    }
    return {
      ...electricityCapacityChartOption,
      tooltip: {
        ...electricityCapacityChartOption.tooltip,
        textStyle: {
          fontFamily: '"Poppins", sans-serif',
          fontSize: 14
        }
      },
      legend: {
        ...electricityCapacityChartOption.legend,
        bottom: 50,
        textStyle: {
          ...electricityCapacityChartOption.legend.textStyle,
          fontSize: 14
        }
      },
      grid: {
        left: '12%', // Increased to ensure yAxis label is always visible
        right: '12%', // Increased to ensure yAxis label is always visible
        bottom: '30%',
        top: '10%',
        containLabel: true
      },
      xAxis: {
        ...electricityCapacityChartOption.xAxis,
        axisLabel: {
          ...electricityCapacityChartOption.xAxis.axisLabel,
          fontSize: 14
        }
      },
      yAxis: {
        ...electricityCapacityChartOption.yAxis,
        axisLabel: {
          ...electricityCapacityChartOption.yAxis.axisLabel,
          fontSize: 14
        },
        nameTextStyle: {
          ...electricityCapacityChartOption.yAxis.nameTextStyle,
          fontSize: 14
        }
      }
    };
  }, [electricityCapacityChartOption]);



  // ECharts options for renewable energy generation mix chart
  const renewableEnergyGenerationMixChartOption = useMemo(() => {
    if (!renewableEnergyGenerationMixData || renewableEnergyGenerationMixData.length === 0) {
      return {};
    }
    const years = renewableEnergyGenerationMixData.map(item => item.year.toString());
    const swtwo = renewableEnergyGenerationMixData.map(item => item.swtwo || null);
    const hydropower = renewableEnergyGenerationMixData.map(item => item.hydropower || null);
    const biofuelsWaste = renewableEnergyGenerationMixData.map(item => item.biofuelsWaste || null);
    const geothermal = renewableEnergyGenerationMixData.map(item => item.geothermal || null);
    return {
      tooltip: {
        trigger: 'axis',
        axisPointer: {
          type: 'line'
        },
        formatter: function (params) {
          if (!params || !Array.isArray(params) || params.length === 0) {
            return '';
          }
          const year = params[0].axisValue || params[0].name || '';
          let items = '';
          params.forEach(function (param, index) {
            const val = param.value;
            const value = (val !== null && val !== undefined && !isNaN(val))
              ? val.toLocaleString('en-US', { maximumFractionDigits: 0 })
              : 'N/A';
            const isLast = index === params.length - 1;
            const borderStyle = isLast ? 'border-top: 1px solid #eee; padding-top: 4px; margin-top: 4px;' : '';
            items += '<div style="color: #666; font-size:14px; font-weight:500; margin-bottom: 4px; font-family: \'Poppins\', sans-serif; ' + borderStyle + '">' +
              '<span style="color:' + (param.color || '#32CD32') + '">●</span> ' +
              param.seriesName + ': <span style="font-weight: 600; color: #333;">' + value + ' GWh</span>' +
              '</div>';
          });
          return '<div style="padding: 5px; background: white; border-radius: 8px; font-family: \'Poppins\', sans-serif;">' +
            '<div style="font-size: 14px; font-weight: 500; color: #333; margin-bottom: 8px; font-family: \'Poppins\', sans-serif;">' +
            year + '</div>' + items + '</div>';
        },
        backgroundColor: 'rgba(255, 255, 255, 0.98)',
        borderColor: '#e5e5e5',
        borderWidth: 1,
        borderRadius: 8,
        textStyle: {
          fontFamily: "'Poppins', sans-serif",
          fontSize: 14,
          color: '#666666',
          fontWeight: '500'
        }
      },
      legend: {
        data: ['Electricity generated from solar, wind, tide, wave and other sources (GWh)', 'Electricity generated from hydropower (GWh)', 'Electricity generated from biofuels and waste (GWh)', 'Electricity generated from geothermal energy (GWh)'],
        type: 'scroll', // allows horizontal scrolling if too wide
        orient: 'horizontal', // force horizontal layout
        bottom: 50, // position legend at bottom
        left: 'center', // center align
        itemWidth: 12,
        itemHeight: 12,
        itemGap: 20, // space between legend items
        textStyle: {
          fontFamily: "'Poppins', sans-serif",
          fontSize: 14,
          color: '#181818',
          fontWeight: 'normal'
        },
        icon: 'circle',
      },
      grid: {
        left: '3%',
        right: '4%',
        bottom: '30%',
        top: '10%',
        containLabel: true
      },
      dataZoom: [
        {
          type: 'slider',
          show: true,
          xAxisIndex: [0],
          start: getDataZoomRange(years)[0],
          end: getDataZoomRange(years)[1],
          bottom: 15,
          height: 20,
          backgroundColor: '#f8f9fa',
          borderColor: '#e9ecef',
          fillerColor: 'rgba(135, 206, 250, 0.3)',
          selectedDataBackground: {
            areaStyle: {
              color: 'rgba(135, 206, 250, 0.2)'
            },
            lineStyle: {
              color: '#87ceeb',
              width: 1
            }
          },
          handleStyle: {
            color: '#87ceeb',
            borderColor: '#87ceeb',
            borderWidth: 1,
            shadowBlur: 2,
            shadowColor: 'rgba(0, 0, 0, 0.1)'
          },
          textStyle: {
            color: '#666',
            fontSize: 14,
            fontFamily: "'Poppins', sans-serif"
          },
          showDetail: false,
          showDataShadow: false,
          realtime: true,
          filterMode: 'filter'
        },
        {
          type: 'inside',
          xAxisIndex: [0],
          start: getDataZoomRange(years)[0],
          end: getDataZoomRange(years)[1],
          zoomLock: true,
          zoomOnMouseWheel: false,
          moveOnMouseMove: false,
          moveOnMouseWheel: false
        }
      ],
      xAxis: {
        type: 'category',
        data: years,
        axisLine: {
          lineStyle: {
            color: '#e5e5e5'
          }
        },
        axisTick: {
          show: false
        },
        axisLabel: {
          color: '#666',
          fontSize: 14,
        },
      },
      yAxis: {
        type: 'value',
        name: 'GWh',
        nameLocation: 'middle',
        nameGap: 50,
        axisLine: {
          lineStyle: {
            color: '#e5e5e5'
          }
        },
        axisTick: {
          show: false
        },
        axisLabel: {
          color: '#666',
          fontSize: 14,
          fontFamily: '"Poppins", sans-serif',
          show: true
        },
        nameTextStyle: {
          color: '#666',
          fontSize: 14,
          fontFamily: '"Poppins", sans-serif',
          padding: [0, 0, 0, 0]
        }
      },
      series: [
        {
          name: 'Electricity generated from solar, wind, tide, wave and other sources (GWh)',
          type: 'bar',
          stack: 'total',
          data: swtwo,
          itemStyle: {
            color: '#FFC50F'
          }
        },
        {
          name: 'Electricity generated from hydropower (GWh)',
          type: 'bar',
          stack: 'total',
          data: hydropower,
          itemStyle: {
            color: '#1C6EA4'
          }
        },
        {
          name: 'Electricity generated from biofuels and waste (GWh)',
          type: 'bar',
          stack: 'total',
          data: biofuelsWaste,
          itemStyle: {
            color: "#437057"
          }
        },
        {
          name: 'Electricity generated from geothermal energy (GWh)',
          type: 'bar',
          stack: 'total',
          data: geothermal,
          itemStyle: {
            color: '#E14434'
          }
        }
      ],
      media: [
        {
          query: { maxWidth: 768 }, // Mobile
          option: {
            grid: {
              left: '12%', // Increased to ensure yAxis label is always visible on mobile
              right: '8%',
              bottom: '25%'
            },
            legend: {
              bottom: 40,
              itemWidth: 10,
              itemHeight: 10,
              textStyle: { fontSize: 11 }
            },
            xAxis: {
              axisLabel: { fontSize: 11 }
            },
            yAxis: {
              name: 'GWh',
              nameGap: 35, // Reduced gap for mobile
              axisLabel: { fontSize: 11, show: true },
              nameTextStyle: { fontSize: 11, padding: [0, 0, 0, 0] }
            }
          }
        }
      ]
    };
  }, [renewableEnergyGenerationMixData]);


  // ECharts options for modal view (larger fonts)
  const renewableEnergyGenerationMixChartOptionModal = useMemo(() => {
    if (!renewableEnergyGenerationMixChartOption || Object.keys(renewableEnergyGenerationMixChartOption).length === 0) {
      return {};
    }
    return {
      ...renewableEnergyGenerationMixChartOption,
      legend: {
        ...renewableEnergyGenerationMixChartOption.legend,
        type: 'scroll', // allows horizontal scrolling if too wide
        orient: 'horizontal', // force horizontal layout
        bottom: 50, // position legend above dataZoom
        left: 'center', // center align
        itemWidth: 12,
        itemHeight: 12,
        itemGap: 20, // space between legend items
        textStyle: {
          fontFamily: '"Poppins", sans-serif',
          fontSize: 14,
          color: '#181818',
          fontWeight: 'normal'
        },
        icon: 'circle',
      },
      grid: {
        left: '8%', // Increased to ensure yAxis label is always visible
        right: '8%', // Increased to ensure yAxis label is always visible
        bottom: '30%',
        top: '10%',
        containLabel: true
      }
    };
  }, [renewableEnergyGenerationMixChartOption]);


  // Get country flag emoji (simplified - you can enhance this)
  //   const getCountryFlag = (countryName) => {
  //     // Simple mapping for some countries - you can expand this or use a library
  //     const flagMap = {
  //       'Burkina Faso': '🇧🇫',
  //       'Algeria': '🇩🇿',
  //       'Egypt': '🇪🇬',
  //       'Morocco': '🇲🇦',
  //       'Nigeria': '🇳🇬',
  //       'South Africa': '🇿🇦',
  //       'Kenya': '🇰🇪',
  //       'Ethiopia': '🇪🇹',
  //       'Ghana': '🇬🇭',
  //       'Tanzania': '🇹🇿',
  //     };
  //     return flagMap[countryName] || '🌍';
  //   };



  // Prepare chart data for donut charts
  const createDonutData = (percentage, color) => {
    const value = formatPercentage(percentage);
    if (value === null) return null;
    const numValue = parseFloat(value);
    return [
      { name: 'Access', value: numValue, fill: color },
      { name: 'No Access', value: 100 - numValue, fill: '#e5e5e5' }
    ];
  };

  // Track when country actually changes (not just data updates)
  useEffect(() => {
    if (selectedCountry !== prevCountryRef.current) {
      prevCountryRef.current = selectedCountry;
    }
  }, [selectedCountry]);

  // Handle outside click to close dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
    };

    if (isDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isDropdownOpen]);

  // Load Africa GeoJSON
  useEffect(() => {
    fetch('/map/africa.geo.json')
      .then(response => response.json())
      .then(data => {
        setAfricaData(data);
      })
      .catch(error => {
      });
  }, []);

  // Helper function to map GeoJSON country names to API country names
  const mapGeoJSONToAPICountryName = (geoJSONName) => {
    if (!geoJSONName || availableCountries.length === 0) return null;

    // First try direct match
    if (availableCountries.includes(geoJSONName)) {
      return geoJSONName;
    }

    // Try case-insensitive match
    const matched = availableCountries.find(country =>
      country.toLowerCase() === geoJSONName.toLowerCase()
    );
    if (matched) {
      return matched;
    }

    // Try partial match (for cases like "Democratic Republic of the Congo" vs "Congo")
    const partialMatch = availableCountries.find(country =>
      country.toLowerCase().includes(geoJSONName.toLowerCase()) ||
      geoJSONName.toLowerCase().includes(country.toLowerCase())
    );
    if (partialMatch) {
      return partialMatch;
    }

    return null;
  };

  // // Map styling function for countries
  // const getCountryStyle = (feature) => {
  //   // Use 'name' property first (as DataGlance does)
  //   const geoJSONName = feature.properties.name ||
  //     feature.properties.NAME ||
  //     feature.properties.admin ||
  //     feature.properties.NAME_EN ||
  //     feature.properties.ADMIN;



  //   const countryName = mapGeoJSONToAPICountryName(geoJSONName);
  //   const isAvailable = countryName !== null;
  //   const isSelected = selectedCountry === countryName;

  //   if (!isAvailable) {
  //     return {
  //       fillColor: '#f7deb4',
  //       weight: 1,
  //       opacity: 0.2,
  //       color: '#CCCCCC',
  //       fillOpacity: 1,
  //       dashArray: '3,3'
  //     };
  //   }

  //   return {
  //     fillColor: isSelected ? '#d48019' : '#F39C12',
  //     weight: isSelected ? 2.5 : 1.2,
  //     fillOpacity: isSelected ? 0.9 : 0.3,
  //     color: '#d48019',
  //     opacity: 1,
  //     dashArray: isSelected ? null : '3,3',
  //   };
  // };

  const getCountryStyle = (feature) => {
    const countryName = feature.properties.name;
    // console.log('countryName: - ', countryName)
    // const isAvailable = availableCountries.includes(countryName);
    // if (!isAvailable) {
    //   // No fill, but strong orange border (dashed)
    //   return {
    //     fillColor: '#fff',
    //     weight: 1.6,
    //     opacity: 1,
    //     color: '#D48019',
    //     fillOpacity: 1,
    //     dashArray: '3,3'
    //   };
    // }
    // Reverted: use prior color scheme for country polygons
    return {
      fillColor: selectedCountry === countryName ? '#F39C12' : '#F39C12',
      weight: selectedCountry === countryName ? 1.8 : 1.2,
      fillOpacity: selectedCountry === countryName ? 1 : 0.25,
      color: '#D48019',
      opacity: 1,
      dashArray: selectedCountry === countryName ? null : '3,3',
    };
  };

  // Handle country clicks on map
  const onEachCountry = (feature, layer) => {
    // Try different property names - DataGlance uses 'name' property
    const geoJSONName = feature.properties.name ||
      feature.properties.NAME ||
      feature.properties.admin ||
      feature.properties.NAME_EN ||
      feature.properties.ADMIN;

    // Map GeoJSON name to API country name
    const countryName = mapGeoJSONToAPICountryName(geoJSONName);

    // Ensure the layer is clickable
    if (layer.options) {
      layer.options.interactive = true;
    }

    // Bind click event to the layer
    layer.on({
      click: (e) => {
        // If availableCountries is empty, try to use the GeoJSON name directly
        // This handles the case where countries haven't loaded yet
        let finalCountryName = countryName;
        // console.log('geoJSONName: - ', geoJSONName)
        // console.log('availableCountries: - ', availableCountries)
        if (!finalCountryName && geoJSONName && availableCountries.length === 0) {
          // If countries haven't loaded, use the GeoJSON name directly
          // The API might accept it, or it will be corrected when countries load
          finalCountryName = geoJSONName;
        }

        // if (finalCountryName) {
          setSelectedCountry(finalCountryName);
          setIsDropdownOpen(false);
          // Update URL with selected country
          navigate(`/country-energy-profile/${countryToUrl(finalCountryName)}`, { replace: true });
        // } 
      }
    });
  };

  // MapContent component to handle map updates
  const MapContent = () => {
    const map = useMap();

    useEffect(() => {
      if (selectedCountry && africaData) {
        // Find the country feature and zoom to it
        const countryFeature = africaData.features.find(f => {
          const name = f.properties.NAME ||
            f.properties.name ||
            f.properties.admin ||
            f.properties.NAME_EN ||
            f.properties.ADMIN;
          return name === selectedCountry;
        });

        if (countryFeature && countryFeature.geometry) {
          // Get bounds of the country
          const bounds = L.geoJSON(countryFeature).getBounds();
          map.fitBounds(bounds, { padding: [50, 50], maxZoom: 5 });
        }
      }
    }, [selectedCountry, africaData, map]);

    return null;
  };

  // Memoize donut charts to prevent unnecessary re-renders and ensure smooth animation
  const donutCharts = useMemo(() => {
    // Always return charts, even during loading (will show previous data)
    if (!countryData) {
      return [];
    }

    return [
      {
        title: 'Population Access (National)',
        subtitle: '(% of population with access)',
        data: createDonutData(countryData.populationAccessNational, '#17a2b8'),
        value: formatPercentage(countryData.populationAccessNational),
        color: '#17a2b8' // Teal/cyan
      },
      {
        title: 'Population Access (Rural)',
        subtitle: '(% of population with access)',
        data: createDonutData(countryData.populationAccessRural, '#28a745'),
        value: formatPercentage(countryData.populationAccessRural),
        color: '#28a745' // Green
      },
      {
        title: 'Population Access (Urban)',
        subtitle: '(% of population with access)',
        data: createDonutData(countryData.populationAccessUrban, '#007bff'),
        value: formatPercentage(countryData.populationAccessUrban),
        color: '#007bff' // Blue
      },
      {
        title: 'Access to Clean Cooking',
        subtitle: '(% of population with access)',
        data: createDonutData(countryData.cleanCookingAccess, '#9b59b6'),
        value: formatPercentage(countryData.cleanCookingAccess),
        color: '#9b59b6' // Purple
      }
    ];
  }, [countryData, selectedYear]);

  return (
    <>
      <Helmet>
        <title>Africa Energy Trends |Country Energy Profiles</title>
        <meta property="og:title" content="Africa Energy Trends |Country Energy Profiles" />
        <meta property="og:description" content="Explore energy profiles of African countries, covering electricity access, clean cooking adoption, and renewable energy across the African continent." />
        <meta property="og:image" content={`${SITE_BASE_URL}/og-images/country-profile.png`} />
        <meta property="og:image:secure_url" content={`${SITE_BASE_URL}/og-images/country-profile.png`} />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:url" content={`${SITE_BASE_URL}/country-energy-profile`} />
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="Africa Energy Access Trends" />
        <meta name="description" content="Explore energy profiles of African countries, covering electricity access, clean cooking adoption, and renewable energy across the African continent." />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Africa Energy Trends |Country Energy Profiles" />
        <meta name="twitter:description" content="Explore energy profiles of African countries, covering electricity access, clean cooking adoption, and renewable energy across the African continent." />
        <meta name="twitter:image" content={`${SITE_BASE_URL}/og-images/country-profile.png`} />
      </Helmet>
      <div className="country-energy-profile-page">
        {/* App Loader - Shows only on initial page load */}
      {loading && (
        <div className="app-loader">
          <div className="loader-container">
            <img 
              src="/spinner/circle-spinner.gif" 
              alt="Loading..." 
              className="loader-spinner"
            />
          </div>
        </div>
      )}
      <div className="profile-container">
        {/* Main Card - Everything in one card */}
        {/* <div className="main-card"> */}

        {/* Profile Title */}
        {selectedCountry && (
          <div className="profile-title-section">
            <h1 className="profile-title">
              {selectedCountry} Profile: <span style={{ color: '#d17c1b' }}>A Comprehensive Overview</span>
            </h1>
          </div>
        )}

        {/* Top Section: Country Selector */}
        <div className="top-section">
          <div className="country-selector">
            {/* <div className="selector-label">SELECT COUNTRY</div> */}
            {selectedCountry && (
              <div className="country-selector-wrapper" ref={dropdownRef}>
                <button
                  className="country-dropdown-button"
                  onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                  type="button"
                >
                  <div className="selected-country-content">
                    <span className="selected-country-text">{selectedCountry}</span>
                    {selectedCountry && countryToISO(selectedCountry) && (
                      <div className="selected-country-flag">
                        <Flag code={countryToISO(selectedCountry)} />
                      </div>
                    )}
                  </div>
                  <div className={`dropdown-icon ${isDropdownOpen ? 'open' : ''}`}>
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                      <path d="M4 6L8 10L12 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  </div>
                </button>
                {isDropdownOpen && (
                  <div className="country-dropdown-grid">
                    {availableCountries.map(country => {
                      const isoCode = countryToISO(country);
                      return (
                        <div
                          key={country}
                          className={`country-grid-item ${selectedCountry === country ? 'selected' : ''}`}
                          onClick={() => {
                            setSelectedCountry(country);
                            setIsDropdownOpen(false);
                            // Update URL with selected country (lowercase, hyphenated)
                            if (country) {
                              navigate(`/country-energy-profile/${countryToUrl(country)}`, { replace: true });
                            } else {
                              navigate('/country-energy-profile', { replace: true });
                            }
                          }}
                        >
                          {isoCode && (
                            <div className="country-flag-small">
                              <Flag code={isoCode} />
                            </div>
                          )}
                          <span className="country-name">{country}</span>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}

             {/* Year Filter */}
             {availableYears.length > 0 && (
                <div className="year-filter-container">
                  <label htmlFor="year-select" className="year-filter-label">Year:</label>
                  <select
                    id="year-select"
                    value={selectedYear || ''}
                    onChange={(e) => {
                      setSelectedYear(Number(e.target.value));
                    }}
                    className="year-filter-dropdown"
                  >
                    {[...new Set(availableYears.map(year => Number(year)))].sort((a, b) => b - a).map(year => (
                      <option key={year} value={year}>
                        {year}
                      </option>
                    ))}
                  </select>
                </div>
              )}
          </div>
        </div>



        {/* Dashboard Content: Map on Left, Data Cards on Right */}
        <div className={`dashboard-content ${!africaData ? 'no-map' : ''}`} style={{ marginTop: '-3rem' }}>
          {/* Left side - Map */}
          {africaData ? (
            <div className="map-section">
              <div className="map-container">
                <MapContainer
                  center={[0, 22]}
                  zoom={isMobile ? 4.5 : 2.0} 
                  minZoom={isMobile ? 2.5 : 2.0}
                  maxZoom={isMobile ? 2.5 : 2.0}
                  style={{ height: '380px', width: '350px', minWidth: '350px', maxWidth: '350px' }}
                  zoomControl={false}
                  attributionControl={false}
                  scrollWheelZoom={false}
                  dragging={false}
                  doubleClickZoom={false}
                  keyboard={false}
                  bounds={[[-37, -20], [40, 55]]}
                  boundsOptions={{ padding: [10, 10] }}
                >
                  <GeoJSON
                    key="country-geojson"
                    data={africaData}
                    style={getCountryStyle}
                    onEachFeature={onEachCountry}
                  />
                </MapContainer>
              </div>
            </div>
          ) : (
            <div className="map-placeholder"></div>
          )}

          {/* Right side -  Data Cards and Region Title Section*/}
            {/* Data Cards Grid */}
            <div className="data-cards">
              <div className="selected-region-country">
                {selectedCountry ? (
                  <>
                    <span className="selected-label">Region:</span> <span className="selected-area">{countryRegion}</span>
                  </>
                ) : (
                  <div style={{ fontSize: '12px', color: '#666' }}>
                    🏳️ Select a country to see data
                  </div>
                )}
              </div>

              {/* 1. Electricity Generation */}
              <div className="data-card">
                <div className="card-icon">
                  <img src="/images/data-glance-icons/energy-access.png" alt="Energy access icon" width="60" height="60" />
                </div>
                <div className="card-content">
                  <h3 className="card-title">Electricity Generation</h3>
                  <div className="card-value">
                    {loading ? '...' : animatedElectricityGeneration}
                    <span className="card-unit">(GWh)</span>
                  </div>
                  <div className="card-description">
                    Total energy production
                  </div>
                </div>
              </div>


              {/* Total Commitments */}
              <div className="data-card">
                <div className="card-icon">
                  <img src="/images/data-glance-icons/investmade.png" alt="Energy investment commitments icon" width="60" height="60" />
                </div>
                <div className="card-content">
                  <h3 className="card-title">Total Energy Investments</h3>
                  <div className="card-value">
                    {animatedTotalCommitments || '$0'}
                  </div>
                  <div className="card-description">
                  Total value of all investments across energy projects.
                  </div>
                </div>
              </div>


              {/* Total Projects Count */}
              <div className="data-card">
                <div className="card-icon">
                  <img src="/images/data-glance-icons/project.png" alt="Energy project icon" width="60" height="60" />
                </div>
                <div className="card-content">
                  <h3 className="card-title">Total Projects</h3>
                  <div className="card-value">
                    {loading ? '...' : animatedTotalProjectsCount}
                    {dashboardData.totalProjectsCount && <span className="card-unit">Projects</span>}
                  </div>
                  {/* <div className="card-description">
                    Total consumption
                  </div> */}
                </div>
              </div>


              {/* 3. GDP */}
              <div className="data-card">
                <div className="card-icon">
                  <img src="/images/data-glance-icons/gdp.png" alt="GDP economic indicator icon" width="60" height="60" />
                </div>
                <div className="card-content">
                  <h3 className="card-title">GDP</h3>
                  <div className="card-value">
                    {loading ? '...' : animatedGDP}
                    <span className="card-unit">(Million)</span>
                  </div>
                  <div className="card-description">
                    Economic indicator
                  </div>
                </div>
              </div>


              {/* 4. Population */}
              <div className="data-card">
                <div className="card-icon">
                  <img src="/images/data-glance-icons/population.png" alt="Population demographic icon" width="60" height="60" />
                </div>
                <div className="card-content">
                  <h3 className="card-title">Population</h3>
                  <div className="card-value">
                    {loading ? '...' : animatedPopulation}
                    {dashboardData.population && <span className="card-unit">(Million)</span>}
                  </div>
                  <div className="card-description">
                    <div className="population-toggle">
                      <span className={`toggle-label ${populationAccessToggle === 'access' ? 'active' : ''}`}>Access</span>
                      <div
                        className={`toggle-switch ${populationAccessToggle === 'without' ? 'without-active' : ''}`}
                        onClick={() => setPopulationAccessToggle(populationAccessToggle === 'access' ? 'without' : 'access')}
                      >
                        <div className="toggle-handle"></div>
                      </div>
                      <span className={`toggle-label ${populationAccessToggle === 'without' ? 'active' : ''}`}>Without Access</span>
                    </div>
                    {populationAccessToggle === 'access' ? (
                      <div className="population-breakdown">
                        Urban: <strong>{animatedPopulationWithAccessUrban}</strong><span style={{fontSize:'7px'}}>(M)</span> Rural: <strong>{animatedPopulationWithAccessRural}</strong><span style={{fontSize:'7px'}}>(M)</span>
                      </div>
                    ) : (
                      <div className="population-breakdown">
                        Urban: <strong>{animatedPopulationWithoutAccessUrban}</strong><span style={{fontSize:'7px'}}>(M)</span> Rural: <strong>{animatedPopulationWithoutAccessRural}</strong><span style={{fontSize:'7px'}}>(M)</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* 2. Electricity Import/Export */}
              <div className="data-card">
                <div className="card-icon">
                  <img src="/images/data-glance-icons/electricity-import.png" alt="Electricity import export icon" width="60" height="60" />
                </div>
                <div className="card-content">
                  <h3 className="card-title">Electricity Import/Export</h3>
                  <div className="card-value">
                    {loading ? '...' : (
                      electricityTradeToggle === 'import'
                        ? animatedElectricityImport
                        : animatedElectricityExport
                    )}
                    <span className="card-unit">(GWh)</span>
                  </div>
                  <div className="card-description">
                    <div className="trade-toggle">
                      <span className={`toggle-label ${electricityTradeToggle === 'import' ? 'active' : ''}`}>Import</span>
                      <div
                        className={`toggle-switch ${electricityTradeToggle === 'export' ? 'export-active' : ''}`}
                        onClick={() => setElectricityTradeToggle(electricityTradeToggle === 'import' ? 'export' : 'import')}
                      >
                        <div className="toggle-handle"></div>
                      </div>
                      <span className={`toggle-label ${electricityTradeToggle === 'export' ? 'active' : ''}`}>Export</span>
                    </div>
                  </div>
                </div>
              </div>


              {/* 5. Clean Cooking */}
              <div className="data-card">
                <div className="card-icon">
                  <img src="/images/data-glance-icons/clean-cooking.png" alt="Clean cooking energy icon representing access to modern cooking solutions" width="60" height="60" />
                </div>
                <div className="card-content">
                  <h3 className="card-title">Clean Cooking</h3>
                  <div className="card-value">
                    {loading ? '...' : animatedCleanCooking}
                    {dashboardData.cleanCooking && <span className="card-unit">(%)</span>}
                  </div>
                  <div className="card-description">
                    Clean cooking access
                  </div>
                </div>
              </div>

              {/* 6. Per Capita Electricity */}
              <div className="data-card">
                <div className="card-icon">
                  <img src="/images/data-glance-icons/per-capita.png" alt="Per capita electricity consumption icon" width="60" height="60" />
                </div>
                <div className="card-content">
                  <h3 className="card-title">Per Capita Electricity</h3>
                  <div className="card-value">
                    {loading ? '...' : animatedPerCapitaElectricity}
                    {dashboardData.perCapitaElectricity && <span className="card-unit">(kWh)</span>}
                  </div>
                  <div className="card-description">
                    Total consumption
                  </div>
                </div>
              </div>

              {/* 7. View All Projects - Only show when country is selected */}
              {selectedCountry && (
                <div 
                  className="data-card clickable-card"
                  onClick={() => {
                    navigate(`/projects?country=${encodeURIComponent(selectedCountry)}`);
                  }}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      navigate(`/projects?country=${encodeURIComponent(selectedCountry)}`);
                    }
                  }}
                  aria-label={`View all projects for ${selectedCountry}`}
                >
                  <div className="card-icon">
                    <img src="/images/data-glance-icons/view-more.png" alt="View all projects icon" width="60" height="60" />
                  </div>
                  <div className="card-content">
                    <h3 className="card-title">View All Projects</h3>
                    <button
                      className="read-more-button"
                      onClick={(e) => {
                        e.stopPropagation(); // Prevent double navigation
                        navigate(`/projects?country=${encodeURIComponent(selectedCountry)}`);
                      }}
                    >
                      <span className="read-more-text">Click Here</span>
                      <span className="read-more-arrow-circle">
                        <svg width="8" height="8" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M4.5 3L7.5 6L4.5 9" stroke="#FFFFFF" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                      </span>
                    </button>
                    <div className="card-description">
                      Explore all projects in {selectedCountry}
                    </div>
                  </div>
                </div>
              )}
            </div>
        </div>

        {/* Energy Access Metrics Cards */}
        {/* <div className="metrics-grid">
            {donutCharts.map((chart, index) => (
              <div key={index} className="metric-card">
                <div className="metric-header">
                  <h3 className="metric-title">{chart.title}</h3>
                  <p className="metric-subtitle">{chart.subtitle}</p>
                  <p className="metric-year">Year : {selectedYear}</p>
                </div>
                {chart.data && chart.value !== null ? (
                  <div className="metric-chart">
                    <ResponsiveContainer width="100%" height={200}>
                      <PieChart>
                        <Pie
                          key={`pie-${selectedCountry}-${selectedYear}-${index}`}
                          data={chart.data}
                          cx="50%"
                          cy="50%"
                          innerRadius={60}
                          outerRadius={85}
                          paddingAngle={0}
                          dataKey="value"
                          startAngle={90}
                          endAngle={-270}
                          isAnimationActive={true}
                          animationBegin={0}
                          animationDuration={1000}
                          animationEasing="ease-out"
                          isUpdateAnimationActive={true}
                        >
                          {chart.data.map((entry, i) => (
                            <Cell key={`cell-${i}`} fill={entry.fill} />
                          ))}
                        </Pie>
                      </PieChart>
                    </ResponsiveContainer>
                    <div className="chart-center-value">
                      <span className="center-percentage">{chart.value}</span>
                    </div>
                  </div>
                ) : (
                  <div className="metric-no-data">
                    <p>No data available</p>
                  </div>
                )}
              </div>
            ))}
          </div> */}

      </div>

       {/* Key Indicators Section */}
        <div className="key-indicators-section">
          <div className="container">
          <div className="key-indicators-header">
              <h2 className="key-indicators-title">Key <span style={{ color: '#d17c1b'}}>Indicators</span></h2>
              <div className="key-indicators-underline"></div>
          </div>

          <div className="key-indicators-content">
            {/* Unified Grid Container for All Charts and Tables */}
            <div className="unified-charts-grid">
            {/* Electricity Supply Section */}
            {!loadingElectricityCapacityData && electricityCapacityData && electricityCapacityData.length > 0 && (
                <>
                  {/* Electricity Generation Table - Show with year filter, hide if no data */}
                  {selectedYear && availableYears.length > 0 && (loadingElectricityGenerationTable || electricityGenerationTableData.length > 0) && (
                    <div className="electricity-generation-table-section">
                      <div className="table-header">
                        <h3 className="table-title">Electricity Supply</h3>
                        <div className="table-controls">
                            <div className="table-year-filter">
                              <label htmlFor="table-year-select">Year:</label>
                              <select
                                id="table-year-select"
                              value={tableYear || selectedYear || ''}
                                onChange={(e) => {
                                  setTableYear(Number(e.target.value));
                                }}
                                className="table-year-dropdown"
                              >
                              {availableYears.map(year => (
                                  <option key={year} value={year}>
                                    {year}
                                  </option>
                                ))}
                              </select>
                            </div>
                        </div>
                      </div>

                      {loadingElectricityGenerationTable ? (
                        <div className="table-loading">
                          <p>Loading table data...</p>
                        </div>
                      ) : electricityGenerationTableData.length > 0 ? (
                        <div className="electricity-generation-table-container">
                          <table className="electricity-generation-table">
                            <thead>
                              <tr>
                                <th>Source</th>
                                <th>Electricity Generation (in GWH)</th>
                              </tr>
                            </thead>
                            <tbody>
                              {electricityGenerationTableData.map((row, index) => (
                                <React.Fragment key={index}>
                                  <tr>
                                    <td>
                                      <div className="source-cell">
                                        <span className={`source-icon ${row.iconClass || ''}`}>
                                          <SVGIcon
                                            src={row.icon}
                                            className={row.iconClass || ''}
                                            color={row.color || '#333'}
                                            alt={row.source}
                                          />
                                        </span>
                                        <span className="source-name">{row.source}</span>
                                        {row.isSolar && (
                                          <button
                                            className="expand-toggle"
                                            onClick={() => setExpandedSolar(!expandedSolar)}
                                            aria-label={expandedSolar ? 'Collapse' : 'Expand'}
                                          >
                                            {expandedSolar ? '−' : '+'}
                                          </button>
                                        )}
                                      </div>
                                    </td>
                                    <td>
                                      <span className="generation-value">
                                        {formatNumber(row.value)}
                                      </span>
                                    </td>
                                  </tr>
                                  {row.isSolar && expandedSolar && row.solarSubItems && row.solarSubItems.map((subItem, subIndex) => (
                                    <tr key={`solar-${index}-${subIndex}`} className="solar-sub-item">
                                      <td>
                                        <div className="source-cell">
                                          <span className={`source-icon`}>
                                            {/* <SVGIcon 
                                              src={subItem.icon} 
                                              className={subItem.iconClass || ''}
                                              color={subItem.color || '#333'}
                                              alt={subItem.source}
                                            /> */}
                                          </span>
                                          <span className="source-name">{subItem.source}</span>
                                        </div>
                                      </td>
                                      <td>
                                        <span className="generation-value">
                                          {formatNumber(subItem.value)}
                                        </span>
                                      </td>
                                    </tr>
                                  ))}
                                </React.Fragment>
                              ))}
                              <tr className="total-row">
                                <td>Total</td>
                                <td>
                                  <span className="total-value">
                                    {formatNumber(
                                      electricityGenerationTableData.reduce((sum, row) => {
                                        const numValue = typeof row.value === 'string' ? parseFloat(row.value) : row.value;
                                        return sum + (isNaN(numValue) ? 0 : numValue);
                                      }, 0)
                                    )}
                                  </span>
                                </td>
                              </tr>
                            </tbody>
                          </table>
                        </div>
                      ) : null}
                    </div>
                  )}

                  {/* Electricity Installed Capacity Chart */}
                  {loadingElectricityCapacityData ? (
                    <div className="charts-loading">
                      <p>Loading chart data...</p>
                    </div>
                  ) : electricityCapacityData && electricityCapacityData.length > 0 ? (
                    <div className="chart-container">
                      <div className="chart-header">
                        <h3 className="chart-title">Electricity Installed Capacity, Total (MW)</h3>
                        {!isMobile && (
                        <button
                          className="chart-maximize-btn"
                          onClick={() => setMaximizedChart('electricity-capacity')}
                          title="Maximize chart"
                        >
                          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M8 3H5C3.89543 3 3 3.89543 3 5V8M21 8V5C21 3.89543 20.1046 3 19 3H16M16 21H19C20.1046 21 21 20.1046 21 19V16M3 16V19C3 20.1046 3.89543 21 5 21H8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                          </svg>
                        </button>
                        )}
                      </div>
                      <div className="chart-wrapper">
                        <ReactECharts
                          option={electricityCapacityChartOption}
                          style={{ height:'320px', width: '100%' }}
                        />
                      </div>
                    </div>
                  ) : (
                    <div className="charts-loading">
                      <p>No chart data available.</p>
                    </div>
                  )}
                </>
              )}

             

              {/* Electricity Access Section */}
              {(!loadingElectricityData && 
                !allElectricityAccessValuesAreNA &&
                ((withAccessChartOption && electricityAccessData && electricityAccessData.length > 0) || 
                (withoutAccessChartOption && electricityAccessData && electricityAccessData.length > 0))
              ) && (
                loadingElectricityData ? (
                  <div className="charts-loading">
                    <p>Loading chart data...</p>
                  </div>
                ) : (
                        <div className="chart-container">
                          <div className="chart-header">
                      <h3 className="chart-title">
                        {electricityAccessChartToggle === 'with-access'
                          ? 'Electricity Access - Population'
                          : 'Electricity Access - Population'}
                      </h3>
                      {!isMobile && (
                            <button 
                              className="chart-maximize-btn"
                        onClick={() => setMaximizedChart(electricityAccessChartToggle)}
                              title="Maximize chart"
                            >
                              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M8 3H5C3.89543 3 3 3.89543 3 5V8M21 8V5C21 3.89543 20.1046 3 19 3H16M16 21H19C20.1046 21 21 20.1046 21 19V16M3 16V19C3 20.1046 3.89543 21 5 21H8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                              </svg>
                            </button>
                      )}
                          </div>
                    {/* Toggle Switch - Inside the chart card, below the title */}
                    <div className="electricity-access-toggle-wrapper">
                      <div className="population-toggle">
                        <span className={`toggle-label ${electricityAccessChartToggle === 'with-access' ? 'active' : ''}`}>With Access</span>
                        <div
                          className={`toggle-switch ${electricityAccessChartToggle === 'without-access' ? 'without-active' : ''}`}
                          onClick={() => setElectricityAccessChartToggle(electricityAccessChartToggle === 'with-access' ? 'without-access' : 'with-access')}
                        >
                          <div className="toggle-handle"></div>
                          </div>
                        <span className={`toggle-label ${electricityAccessChartToggle === 'without-access' ? 'active' : ''}`}>Without Access</span>
                          </div>
                          </div>
                          <div className="chart-wrapper">
                            <ReactECharts
                        option={electricityAccessChartToggle === 'with-access' ? withAccessChartOption : withoutAccessChartOption}
                              style={{ height: '320px', width: '100%' }}
                            />
                          </div>
                        </div>
                )
              )}


            {/* Electricity Demand Section */}
            {((!loadingElectricityDemandData || !loadingEnergyIntensityData) && (electricityDemandData && electricityDemandData.length > 0) || (energyIntensityData && energyIntensityData.length > 0)) && (
                (loadingElectricityDemandData || loadingEnergyIntensityData) ? (
                  <div className="charts-loading">
                    <p>Loading chart data...</p>
                  </div>
                ) : (electricityDemandData && electricityDemandData.length > 0) || (energyIntensityData && energyIntensityData.length > 0) ? (
                  <>
                    {/* Chart 1: Final Consumption (GWh) */}
                    {electricityDemandData && electricityDemandData.length > 0 && (
                      <div className="chart-container">
                        <div className="chart-header">
                          <h3 className="chart-title">Electricity Demand - Final Consumption (GWh)</h3>
                          {!isMobile && (
                          <button
                            className="chart-maximize-btn"
                            onClick={() => setMaximizedChart('electricity-demand')}
                            title="Maximize chart"
                          >
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                              <path d="M8 3H5C3.89543 3 3 3.89543 3 5V8M21 8V5C21 3.89543 20.1046 3 19 3H16M16 21H19C20.1046 21 21 20.1046 21 19V16M3 16V19C3 20.1046 3.89543 21 5 21H8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                            </svg>
                          </button>
                          )}
                        </div>
                        <div className="chart-wrapper">
                          <ReactECharts
                            option={electricityDemandChartOption}
                            style={{ height: '320px', width: '100%' }}
                          />
                        </div>
                      </div>
                    )}

                    {/* Chart 2: Energy Intensity */}
                    {energyIntensityData && energyIntensityData.length > 0 && (
                      <div className="chart-container">
                        <div className="chart-header">
                          <h3 className="chart-title">Electricity Demand - Energy Intensity (MJ/2017 PPP GDP)</h3>
                          {!isMobile && (
                          <button
                            className="chart-maximize-btn"
                            onClick={() => setMaximizedChart('energy-intensity')}
                            title="Maximize chart"
                          >
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                              <path d="M8 3H5C3.89543 3 3 3.89543 3 5V8M21 8V5C21 3.89543 20.1046 3 19 3H16M16 21H19C20.1046 21 21 20.1046 21 19V16M3 16V19C3 20.1046 3.89543 21 5 21H8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                            </svg>
                          </button>
                          )}
                        </div>
                        <div className="chart-wrapper">
                          {Object.keys(energyIntensityChartOption).length > 0 ? (
                            <ReactECharts
                              option={energyIntensityChartOption}
                              style={{ height: '320px', width: '100%' }}
                            />
                          ) : (
                            <div style={{ height: '320px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                              <p>No chart data available</p>
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </>
                  ) : (
                    <div className="charts-loading">
                      <p></p>
                    </div>
                )
            )}

            {/* Energy Efficiency & Clean Cooking Combined Section - Only show if at least one has data */}
            {((!loadingEnergyEfficiencyData && energyEfficiencyData && energyEfficiencyData.length > 0) ||
              (!loadingCleanCookingData && cleanCookingData && cleanCookingData.length > 0)) && (
                  <>
                      {/* Energy Efficiency Chart */}
                      {!loadingEnergyEfficiencyData && energyEfficiencyData && energyEfficiencyData.length > 0 && (
                        <div className="chart-container">
                          <div className="chart-header">
                            <h3 className="chart-title">Energy Efficiency - Evolution of total score of RISE indicator</h3>
                            {!isMobile && (
                            <button
                              className="chart-maximize-btn"
                              onClick={() => setMaximizedChart('energy-efficiency')}
                              title="Maximize chart"
                            >
                              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M8 3H5C3.89543 3 3 3.89543 3 5V8M21 8V5C21 3.89543 20.1046 3 19 3H16M16 21H19C20.1046 21 21 20.1046 21 19V16M3 16V19C3 20.1046 3.89543 21 5 21H8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                              </svg>
                            </button>
                            )}
                          </div>
                          <div className="chart-wrapper">
                            <ReactECharts
                              option={energyEfficiencyChartOption}
                              style={{ height: '320px', width: '100%' }}
                            />
                          </div>
                        </div>
                      )}

                      {/* Clean Cooking Chart */}
                      {!loadingCleanCookingData && cleanCookingData && cleanCookingData.length > 0 && (
                        <div className="chart-container">
                          <div className="chart-header">
                            <h3 className="chart-title">Clean Cooking - Evolution of total score of RISE indicator</h3>
                            {!isMobile && (
                            <button
                              className="chart-maximize-btn"
                              onClick={() => setMaximizedChart('clean-cooking')}
                              title="Maximize chart"
                            >
                              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M8 3H5C3.89543 3 3 3.89543 3 5V8M21 8V5C21 3.89543 20.1046 3 19 3H16M16 21H19C20.1046 21 21 20.1046 21 19V16M3 16V19C3 20.1046 3.89543 21 5 21H8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                              </svg>
                            </button>
                            )}
                          </div>
                          <div className="chart-wrapper">
                            <ReactECharts
                              option={cleanCookingChartOption}
                              style={{ height: '320px', width: '100%' }}
                            />
                          </div>
                        </div>
                      )}
                  </>
              )}
            </div>
          </div>
          </div>
        </div>
        {/* </div> */}

      {/* Maximize Chart Modal */}
      {maximizedChart && (
        <div className="chart-modal-overlay" onClick={() => setMaximizedChart(null)}>
          <div className="chart-modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="chart-modal-header">
              <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', flex: 1 }}>
                <h3 className="chart-modal-title">
                  {(maximizedChart === 'with-access' || maximizedChart === 'without-access')
                    ? (electricityAccessChartToggle === 'with-access'
                      ? 'Electricity Access - Population with access to electricity - Millions of people'
                      : 'Electricity Access - Population without access to electricity - Millions of people')
                    : maximizedChart === 'clean-cooking'
                      ? 'Clean Cooking - Evolution of total score of RISE indicator'
                      : maximizedChart === 'energy-efficiency'
                        ? 'Energy Efficiency - Evolution of total score of RISE indicator'
                        : maximizedChart === 'energy-intensity'
                          ? 'Electricity Demand - Energy intensity level of primary energy (MJ/2017 PPP GDP)'
                          : maximizedChart === 'electricity-capacity'
                            ? 'Electricity installed capacity, Total (MW)'
                                  : maximizedChart === 'renewable-energy-generation-mix'
                                    ? 'Renewable Energy Generation Mix'
                                    : maximizedChart === 'electricity-demand'
                                      ? 'Electricity Demand - Final Consumption (GWh)'
                                      : ''
                  }
                </h3>
               
              </div>
              <button
                className="chart-modal-close"
                onClick={() => setMaximizedChart(null)}
                title="Close"
              >
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </button>
            </div>
            <div className="chart-modal-body">
               {/* Toggle Switch for Electricity Access Charts */}
               {(maximizedChart === 'with-access' || maximizedChart === 'without-access') && (
                  <div className="population-toggle maxPopulationToggle" style={{ margin: 0 }}>
                    <span className={`toggle-label ${electricityAccessChartToggle === 'with-access' ? 'active' : ''}`}>With Access</span>
                    <div
                      className={`toggle-switch ${electricityAccessChartToggle === 'without-access' ? 'without-active' : ''}`}
                      onClick={() => setElectricityAccessChartToggle(electricityAccessChartToggle === 'with-access' ? 'without-access' : 'with-access')}
                    >
                      <div className="toggle-handle"></div>
                    </div>
                    <span className={`toggle-label ${electricityAccessChartToggle === 'without-access' ? 'active' : ''}`}>Without Access</span>
                  </div>
                )}
              <div style={{ flex: 1, minHeight: '380px', display: 'flex', flexDirection: 'column', width: '100%' }}>
                <ReactECharts
                  option={
                    (maximizedChart === 'with-access' || maximizedChart === 'without-access')
                      ? (electricityAccessChartToggle === 'with-access'
                        ? withAccessChartOptionModal
                        : withoutAccessChartOptionModal)
                      : maximizedChart === 'clean-cooking'
                        ? cleanCookingChartOptionModal
                        : maximizedChart === 'energy-efficiency'
                          ? energyEfficiencyChartOptionModal
                          : maximizedChart === 'energy-intensity'
                            ? energyIntensityChartOptionModal
                            : maximizedChart === 'electricity-capacity'
                              ? electricityCapacityChartOptionModal
                                    : maximizedChart === 'renewable-energy-generation-mix'
                                      ? renewableEnergyGenerationMixChartOptionModal
                                      : maximizedChart === 'electricity-demand'
                                        ? electricityDemandChartOptionModal
                                        : {}
                  }
                  style={{ height: '100%', width: '100%', minHeight: '380px' }}
                />
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
    </>
  );
};

export default CountryEnergyProfile;